import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { 
  agregarArmazon, 
  obtenerArmazonesDisponibles,
  obtenerArmazones, 
  editarArmazon, 
  eliminarArmazon,
  obtenerCatalogosCristales,
  crearCatalogoCristalesItem,
  actualizarCatalogoCristalesItem,
  desactivarCatalogoCristalesItem,
  eliminarCatalogoCristalesItem,
  obtenerCatalogosArmazones,
  crearCatalogoArmazonItem,
  actualizarCatalogoArmazonItem,
  desactivarCatalogoArmazonItem,
  eliminarCatalogoArmazonItem,
  procesarPdfFactura,
  cargarArmazonesDesdePdf,
  guardarArmazonesDesdeModal
} from '../api/backend_v2';
import { calcularPrecioEfectivoConDescuento, cargarDescuentoEfectivoSeguro, DESCUENTO_EFECTIVO_DEFAULT } from '../utils/precios';
import { obtenerProveedores, crearProveedor, actualizarProveedor, eliminarProveedor } from '../utils/proveedores';
import NuevoProveedorRapidoModal from '../components/NuevoProveedorRapidoModal';
import { isDemoUser, solicitarRegistroDemo } from '../utils/demoMode';
import { authService } from '../api/auth';
import { MagnifyingGlassIcon, PencilIcon, EyeIcon, ChartBarIcon, PlusIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, BanknotesIcon, CurrencyDollarIcon, BeakerIcon, SunIcon, Squares2X2Icon, ViewColumnsIcon, BriefcaseIcon, EyeDropperIcon, RectangleGroupIcon, ArchiveBoxIcon, ListBulletIcon, ArrowPathIcon, DocumentDuplicateIcon, SwatchIcon, PrinterIcon, CreditCardIcon, TagIcon, XMarkIcon } from '@heroicons/react/24/outline';

// Función para redondear al millar más próximo hacia arriba
const redondearAlMillar = (valor) => {
  if (!valor || valor <= 0) return 0;
  return Math.ceil(valor / 1000) * 1000;
};

// Calcula precio público/efectivo según costo y si el armazón es de sol
const calcularPreciosPdf = (costo, sol) => {
  const costoNum = parseFloat(costo) || 0;
  if (costoNum <= 0) return { precio_publico: 0, precio_efectivo: 0 };
  const multiplicador = sol ? 2.7 : 3.1;
  return {
    precio_publico: redondearAlMillar(costoNum * multiplicador),
    precio_efectivo: redondearAlMillar(costoNum * multiplicador * 0.85)
  };
};

const FILA_VACIA = { modelo: '', color: '', material: '', cantidad: '', medidas: '', tipo_bisagra: '', costo: '', precio_publico: '', precio_efectivo: '', id_proveedor: '', codigo_ean: '', sol: false, en_ml: false };
const normalizar = (v) => String(v || '').trim().toLowerCase();
const MARCAS_CACIC_PDF = ['Vulk', 'Rusty', 'Maria Cher', 'Jellvet'];

const StockArmazones = () => {
  const [armazones, setArmazones] = useState([]);
  const [form, setForm] = useState({
    marca: '',
    modelo: '',
    color: '',
    material: '',
    cantidad: '',
    medidas: '',
    tipo_bisagra: '',
    costo: '',
    precio_publico: '',
    precio_efectivo: '',
    id_proveedor: '',
    codigo_ean: '',
    sol: false,
    en_ml: false
  });
  const [loading, setLoading] = useState(false);
  const [busquedaArmazones, setBusquedaArmazones] = useState('');
  const [paginaArmazones, setPaginaArmazones] = useState(1);
  const [tamanoPaginaArmazones, setTamanoPaginaArmazones] = useState(12);
  const [hoverStockId, setHoverStockId] = useState(null);
  const [stockTouchId, setStockTouchId] = useState(null);
  const [actualizandoStockId, setActualizandoStockId] = useState(null);
  const [editando, setEditando] = useState(false);
  const [armazonEditando, setArmazonEditando] = useState(null);
  const [formEdicion, setFormEdicion] = useState({
    marca: '',
    modelo: '',
    color: '',
    material: '',
    cantidad: '',
    medidas: '',
    tipo_bisagra: '',
    costo: '',
    precio_publico: '',
    precio_efectivo: '',
    id_proveedor: '',
    codigo_ean: '',
    sol: false,
    en_ml: false
  });
  const [descuentoEfectivo, setDescuentoEfectivo] = useState(DESCUENTO_EFECTIVO_DEFAULT);
  const [precioEfectivoManual, setPrecioEfectivoManual] = useState(false);
  const [precioEfectivoManualEdicion, setPrecioEfectivoManualEdicion] = useState(false);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [modoSeleccionMultiple, setModoSeleccionMultiple] = useState(false);
  const [eliminandoLote, setEliminandoLote] = useState(false);
  const [modalAgregarArmazon, setModalAgregarArmazon] = useState(false);

  // Carga masiva por marca
  const [modoMasivo, setModoMasivo] = useState(false);
  const [marcaMasiva, setMarcaMasiva] = useState('');
  const [filasMasivas, setFilasMasivas] = useState([{ ...FILA_VACIA }]);
  const [filasMasivasExpandida, setFilasMasivasExpandida] = useState({});
  const [guardandoMasivo, setGuardandoMasivo] = useState(false);
  const [marcasArmazonesCatalogo, setMarcasArmazonesCatalogo] = useState([]);
  const [modoNuevaMarca, setModoNuevaMarca] = useState({ normal: false, masiva: false, edicion: false });
  const [materialesArmazonesCatalogo, setMaterialesArmazonesCatalogo] = useState([]);
  const [coloresArmazonesCatalogo, setColoresArmazonesCatalogo] = useState([]);

  // Definición de tipos de catálogos para solapas
  const catalogoTiposArmazones = [
    { key: 'marcas', label: 'Marcas' },
    { key: 'materiales', label: 'Materiales' },
    { key: 'colores', label: 'Colores' },
    { key: 'proveedores', label: 'Proveedores' }
  ];

  // Modal unificado de catálogos de armazones con solapas
  const [modalCatalogosArmazones, setModalCatalogosArmazones] = useState(false);
  const [catalogoArmazonActivo, setCatalogoArmazonActivo] = useState('marcas'); // 'marcas' | 'materiales' | 'colores' | 'proveedores'
  const [catalogoArmazonBusqueda, setCatalogoArmazonBusqueda] = useState('');
  const [nuevoCatalogoArmazonNombre, setNuevoCatalogoArmazonNombre] = useState('');
  const [guardandoCatalogoArmazon, setGuardandoCatalogoArmazon] = useState(false);

  // Proveedores
  const [proveedores, setProveedores] = useState([]);
  const [editandoProveedor, setEditandoProveedor] = useState(null);
  const [formProveedor, setFormProveedor] = useState({
    nombre: '',
    nombre_vendedor: '',
    telefono: '',
    direccion: '',
    cuit: '',
    descuento_efectivo: '',
    cuotas_maximas: '',
    marcas: [],
  });
  const [guardandoProveedor, setGuardandoProveedor] = useState(false);
  const [cargandoProveedores, setCargandoProveedores] = useState(false);

  // Solapas de stock
  const [solapaStock, setSolapaStock] = useState('todos'); // 'todos' | 'con-stock' | 'sin-stock'

  // Modal de carga de PDF
  const [modalPdf, setModalPdf] = useState(false);
  const [pdfCargando, setPdfCargando] = useState(false);
  const [pdfArmazones, setPdfArmazones] = useState([]);
  const [pdfAccesorios, setPdfAccesorios] = useState([]);
  const [tabModalPdf, setTabModalPdf] = useState('armazones');
  const [pdfArchivo, setPdfArchivo] = useState(null);
  const [pdfMarca, setPdfMarca] = useState('');
  const [pdfSol, setPdfSol] = useState(false);
  const [pdfIdProveedor, setPdfIdProveedor] = useState('');
  const [pdfGuardando, setPdfGuardando] = useState(false);
  const [pdfResumen, setPdfResumen] = useState(null);

  // Modal de duplicación
  const [modalDuplicar, setModalDuplicar] = useState(false);
  const [formDuplicado, setFormDuplicado] = useState({
    marca: '',
    modelo: '',
    color: '',
    material: '',
    cantidad: '',
    medidas: '',
    tipo_bisagra: '',
    costo: '',
    precio_publico: '',
    precio_efectivo: '',
    id_proveedor: '',
    codigo_ean: '',
    sol: false,
    en_ml: false
  });
  const [guardandoDuplicado, setGuardandoDuplicado] = useState(false);
  const [armazonDuplicando, setArmazonDuplicando] = useState(null);
  const [modoNuevaMarcaDuplicado, setModoNuevaMarcaDuplicado] = useState(false);

  useEffect(() => {
    cargarArmazones();
    cargarCatalogoMarcasArmazones();
    cargarCatalogosArmazones();
    cargarProveedores();

    cargarDescuentoEfectivoSeguro().then(setDescuentoEfectivo);
  }, []);

  const cargarProveedores = async () => {
    try {
      const data = await obtenerProveedores();
      setProveedores(data);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  const calcularPrecioEfectivo = (precioPublico) => {
    return calcularPrecioEfectivoConDescuento(precioPublico, descuentoEfectivo);
  };

  const cargarCatalogoMarcasArmazones = async () => {
    try {
      const res = await obtenerCatalogosCristales(false);
      setMarcasArmazonesCatalogo(res.marcas_armazones || []);
    } catch {
      setMarcasArmazonesCatalogo([]);
    }
  };

  const marcasDisponiblesPdf = useMemo(() => {
    const nombres = [...MARCAS_CACIC_PDF, ...marcasArmazonesCatalogo.map((marca) => marca.nombre)]
      .filter(Boolean)
      .reduce((unicas, nombre) => {
        const clave = normalizar(nombre);
        if (!unicas.some((marca) => normalizar(marca) === clave)) {
          unicas.push(nombre);
        }
        return unicas;
      }, []);
    return nombres;
  }, [marcasArmazonesCatalogo]);

  const cargarCatalogosArmazones = async () => {
    try {
      const res = await obtenerCatalogosArmazones(false);
      setMaterialesArmazonesCatalogo(res.material_armazon || []);
      setColoresArmazonesCatalogo(res.color_armazon || []);
    } catch {
      setMaterialesArmazonesCatalogo([]);
      setColoresArmazonesCatalogo([]);
    }
  };

  // Funciones para manejar proveedores
  const cargarProveedoresModal = async () => {
    setCargandoProveedores(true);
    try {
      const data = await obtenerProveedores();
      setProveedores(data);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    } finally {
      setCargandoProveedores(false);
    }
  };

  const resetFormProveedor = () => {
    setFormProveedor({
      nombre: '',
      nombre_vendedor: '',
      telefono: '',
      direccion: '',
      cuit: '',
      descuento_efectivo: '',
      cuotas_maximas: '',
      marcas: [],
    });
    setEditandoProveedor(null);
  };

  const [modalProveedorRapidoContext, setModalProveedorRapidoContext] = useState(null);

  const handleCrearProveedorRapido = (setterFn, marcaActual = '') => {
    setModalProveedorRapidoContext({ setterFn, marcaActual });
  };

  const handleGuardarProveedor = async (e) => {
    e?.preventDefault();
    if (!formProveedor.nombre.trim()) {
      Swal.fire('Error', 'El nombre del proveedor es requerido', 'error');
      return;
    }

    setGuardandoProveedor(true);
    try {
      const payload = {
        ...formProveedor,
        descuento_efectivo: formProveedor.descuento_efectivo !== '' && !Number.isNaN(parseFloat(formProveedor.descuento_efectivo))
          ? parseFloat(formProveedor.descuento_efectivo)
          : null,
        cuotas_maximas: formProveedor.cuotas_maximas !== '' && !Number.isNaN(parseInt(formProveedor.cuotas_maximas, 10))
          ? parseInt(formProveedor.cuotas_maximas, 10)
          : null,
      };

      if (editandoProveedor) {
        await actualizarProveedor(editandoProveedor.id, payload);
        Swal.fire('Éxito', 'Proveedor actualizado', 'success');
      } else {
        await crearProveedor(payload);
        Swal.fire('Éxito', 'Proveedor creado', 'success');
      }
      await cargarProveedoresModal();
      resetFormProveedor();
    } catch (error) {
      Swal.fire('Error', error.message || 'Error al guardar', 'error');
    } finally {
      setGuardandoProveedor(false);
    }
  };

  const handleEditarProveedor = (proveedor) => {
    setEditandoProveedor(proveedor);
    setFormProveedor({
      nombre: proveedor.nombre,
      nombre_vendedor: proveedor.nombre_vendedor || '',
      telefono: proveedor.telefono || '',
      direccion: proveedor.direccion || '',
      cuit: proveedor.cuit || '',
      descuento_efectivo: proveedor.descuento_efectivo != null ? String(proveedor.descuento_efectivo) : '',
      cuotas_maximas: proveedor.cuotas_maximas != null ? String(proveedor.cuotas_maximas) : '',
      marcas: (proveedor.marcas || []).map((m) => m.id),
    });
  };

  const handleEliminarProveedor = async (proveedor) => {
    const result = await Swal.fire({
      title: '¿Eliminar proveedor?',
      text: `¿Está seguro que desea eliminar "${proveedor.nombre}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await eliminarProveedor(proveedor.id);
        Swal.fire('Eliminado', 'Proveedor eliminado exitosamente', 'success');
        await cargarProveedoresModal();
        resetFormProveedor();
      } catch (error) {
        Swal.fire('Error', error.message || 'Error al eliminar', 'error');
      }
    }
  };

  const handleToggleMarcaProveedor = (marcaId) => {
    setFormProveedor((prev) => ({
      ...prev,
      marcas: prev.marcas.includes(marcaId)
        ? prev.marcas.filter((m) => m !== marcaId)
        : [...prev.marcas, marcaId],
    }));
  };

  // Calcular marcas ya asignadas a otros proveedores
  const marcasAsignadas = useMemo(() => {
    const marcasIds = new Set();
    proveedores.forEach((proveedor) => {
      if (editandoProveedor && proveedor.id === editandoProveedor.id) return;
      if (proveedor.marcas) {
        proveedor.marcas.forEach((marca) => {
          marcasIds.add(marca.id);
        });
      }
    });
    return marcasIds;
  }, [proveedores, editandoProveedor]);

  const marcasDisponiblesFiltradasPorProveedor = useMemo(() => {
    return marcasArmazonesCatalogo.filter((m) => m.activo && !marcasAsignadas.has(m.id));
  }, [marcasArmazonesCatalogo, marcasAsignadas]);

  const marcasArmazonesActivas = (marcasArmazonesCatalogo || [])
    .filter((i) => i.activo)
    .map((i) => i.nombre)
    .filter(Boolean);

  // Obtener proveedor por nombre de marca
  const obtenerProveedorPorMarca = (nombreMarca) => {
    if (!nombreMarca) return null;
    const marcaNormalizada = normalizar(nombreMarca);
    for (const proveedor of proveedores) {
      if (proveedor.marcas && Array.isArray(proveedor.marcas)) {
        const tieneMarca = proveedor.marcas.some(
          (m) => normalizar(m.nombre) === marcaNormalizada
        );
        if (tieneMarca) return proveedor.id;
      }
    }
    return null;
  };

  // Memoized filtered list para el catálogo activo
  const itemsCatalogoArmazonActivo = useMemo(() => {
    let items = [];
    if (catalogoArmazonActivo === 'marcas') items = marcasArmazonesCatalogo || [];
    else if (catalogoArmazonActivo === 'materiales') items = materialesArmazonesCatalogo || [];
    else if (catalogoArmazonActivo === 'colores') items = coloresArmazonesCatalogo || [];
    else if (catalogoArmazonActivo === 'proveedores') items = proveedores || [];

    const term = normalizar(catalogoArmazonBusqueda);
    return [...items]
      .filter((i) => !term || normalizar(i.nombre).includes(term))
      .sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'));
  }, [catalogoArmazonActivo, marcasArmazonesCatalogo, materialesArmazonesCatalogo, coloresArmazonesCatalogo, proveedores, catalogoArmazonBusqueda]);

  const armazonesFiltrados = useMemo(() => {
    let resultado = armazones;
    
    // Filtrar por búsqueda
    const term = normalizar(busquedaArmazones);
    if (term) {
      resultado = resultado.filter((a) => {
        const campos = [
          a.marca,
          a.modelo,
          a.color,
          a.material,
          a.medidas,
          a.tipo_bisagra,
          a.cantidad,
        ].map(normalizar);
        return campos.some((v) => v.includes(term));
      });
    }

    // Filtrar por stock solo en la solapa "sin-stock"
    // (en "con-stock" ya vienen filtrados desde la API)
    if (solapaStock === 'sin-stock') {
      resultado = resultado.filter((a) => parseInt(a.cantidad, 10) === 0);
    }

    // Ordenar: primero los con stock, luego los sin stock; y organizados por marca y modelo
    resultado = resultado.sort((a, b) => {
      const cantidadA = parseInt(a.cantidad, 10);
      const cantidadB = parseInt(b.cantidad, 10);
      const tieneStockA = cantidadA > 0 ? 0 : 1;
      const tieneStockB = cantidadB > 0 ? 0 : 1;
      if (tieneStockA !== tieneStockB) return tieneStockA - tieneStockB;
      const cmpMarca = String(a.marca || '').localeCompare(String(b.marca || ''), 'es', { sensitivity: 'base' });
      if (cmpMarca !== 0) return cmpMarca;
      return String(a.modelo || '').localeCompare(String(b.modelo || ''), 'es', { sensitivity: 'base' });
    });

    return resultado;
  }, [armazones, busquedaArmazones, solapaStock]);

  const totalPaginasArmazones = Math.max(1, Math.ceil(armazonesFiltrados.length / tamanoPaginaArmazones));

  const armazonesPaginados = useMemo(() => {
    const inicio = (paginaArmazones - 1) * tamanoPaginaArmazones;
    return armazonesFiltrados.slice(inicio, inicio + tamanoPaginaArmazones);
  }, [armazonesFiltrados, paginaArmazones, tamanoPaginaArmazones]);

  useEffect(() => {
    setPaginaArmazones(1);
  }, [busquedaArmazones, tamanoPaginaArmazones]);

  useEffect(() => {
    if (paginaArmazones > totalPaginasArmazones) {
      setPaginaArmazones(totalPaginasArmazones);
    }
  }, [paginaArmazones, totalPaginasArmazones]);

  // Recargar armazones cuando cambia la solapa de stock
  useEffect(() => {
    setPaginaArmazones(1);
    cargarArmazones(solapaStock);
  }, [solapaStock]);

  const valorEnCatalogo = (valor) => {
    const target = normalizar(valor);
    if (!target) return true;
    return marcasArmazonesActivas.some((item) => normalizar(item) === target);
  };

  const optionLegado = (valor, keyPrefix) => {
    const limpio = String(valor || '').trim();
    if (!limpio || valorEnCatalogo(limpio)) return null;
    return <option key={`${keyPrefix}-${limpio}`} value={limpio}>{`Valor legado: ${limpio}`}</option>;
  };

  const asegurarMarcaEnCatalogo = async (marca) => {
    const nombre = String(marca || '').trim();
    if (!nombre) return '';
    if (valorEnCatalogo(nombre)) return nombre;
    await crearCatalogoCristalesItem('marcas_armazones', nombre);
    await cargarCatalogoMarcasArmazones();
    return nombre;
  };

  const colorEnCatalogo = (color) => {
    const target = normalizar(color);
    if (!target) return true;
    return (coloresArmazonesCatalogo || [])
      .filter(i => i.activo)
      .some(item => normalizar(item.nombre) === target);
  };

  const asegurarColorEnCatalogo = async (color) => {
    const nombre = String(color || '').trim();
    if (!nombre) return '';
    if (colorEnCatalogo(nombre)) return nombre;
    await crearCatalogoArmazonItem('color_armazon', nombre);
    await cargarCatalogosArmazones();
    return nombre;
  };

  const cargarArmazones = async (filtroStock = solapaStock) => {
    setLoading(true);
    try {
      let res;
      if (filtroStock === 'con-stock') {
        // Traer solo armazones con stock > 0 desde la API
        res = await obtenerArmazonesDisponibles();
      } else {
        // Traer todos los armazones
        res = await obtenerArmazones();
      }
      setArmazones(res.armazones || []);
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Tras cargar un PDF con duplicados, ofrece sumar el stock nuevo al existente por ítem
  const preguntarPorDuplicadosArmazones = async (duplicados) => {
    if (!duplicados || duplicados.length === 0) return;

    const filasHtml = duplicados
      .map((dup, idx) => `
        <label style="display:flex;align-items:center;gap:8px;text-align:left;padding:6px 0;border-bottom:1px solid #eee;">
          <input type="checkbox" id="dup-check-${idx}" checked />
          <span style="font-size:13px;">
            <strong>${dup.marca} ${dup.modelo} ${dup.color}</strong><br/>
            Stock actual: ${dup.cantidad_existente} + nuevo: ${dup.cantidad_nueva} = ${dup.cantidad_existente + dup.cantidad_nueva}
          </span>
        </label>
      `)
      .join('');

    const { isConfirmed, value: seleccionados } = await Swal.fire({
      title: `${duplicados.length} armazón(es) ya existen`,
      html: `
        <p style="font-size:13px;color:#555;margin-bottom:8px;">
          ¿Querés sumar la cantidad nueva al stock existente de estos armazones?
        </p>
        <div style="max-height:300px;overflow-y:auto;">${filasHtml}</div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Agregar seleccionados',
      cancelButtonText: 'Omitir todos',
      focusConfirm: false,
      preConfirm: () => duplicados
        .map((dup, idx) => ({ dup, marcado: document.getElementById(`dup-check-${idx}`)?.checked }))
        .filter((item) => item.marcado)
        .map((item) => item.dup)
    });

    if (!isConfirmed || !seleccionados || seleccionados.length === 0) return;

    let actualizados = 0;
    const errores = [];
    for (const dup of seleccionados) {
      try {
        await editarArmazon(dup.id_existente, { cantidad: dup.cantidad_existente + dup.cantidad_nueva });
        actualizados++;
      } catch (e) {
        errores.push(`${dup.marca} ${dup.modelo} ${dup.color}: ${e.message}`);
      }
    }

    await cargarArmazones();

    if (errores.length === 0) {
      Swal.fire('¡Listo!', `Se sumó stock a ${actualizados} armazón(es).`, 'success');
    } else {
      Swal.fire('Parcialmente actualizado', `${actualizados} actualizados. Errores:\n${errores.join('\n')}`, 'warning');
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (name === 'precio_publico' && !precioEfectivoManual) {
        next.precio_efectivo = calcularPrecioEfectivo(value);
      }
      if (name === 'precio_publico' && String(value || '').trim() === '' && !precioEfectivoManual) {
        next.precio_efectivo = '';
      }

      return next;
    });

    if (name === 'precio_efectivo') {
      setPrecioEfectivoManual(String(value || '').trim() !== '');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (isDemoUser(authService.obtenerUsuarioActual())) {
      solicitarRegistroDemo('crear y cargar armazones en tu inventario');
      return;
    }

    try {
      const marcaFinal = await asegurarMarcaEnCatalogo(form.marca);
      const colorFinal = await asegurarColorEnCatalogo(form.color);

      // Validar que cantidad sea un número
      const cantidad = parseInt(form.cantidad);
      if (isNaN(cantidad) || cantidad < 0) {
        Swal.fire('Error', 'La cantidad debe ser un número válido mayor o igual a 0', 'error');
        return;
      }

      const armazonData = {
        ...form,
        marca: marcaFinal,
        color: colorFinal,
        cantidad: cantidad,
        costo: form.costo ? parseFloat(form.costo) : null,
        precio_publico: form.precio_publico ? parseFloat(form.precio_publico) : null,
        precio_efectivo: form.precio_efectivo ? parseFloat(form.precio_efectivo) : null,
        id_proveedor: form.id_proveedor ? parseInt(form.id_proveedor) : null,
      };

      await agregarArmazon(armazonData);
      Swal.fire('¡Armazón agregado!', '', 'success');
      setModalAgregarArmazon(false);
      setForm({
        marca: '',
        modelo: '',
        color: '',
        material: '',
        cantidad: '',
        medidas: '',
        tipo_bisagra: '',
        costo: '',
        precio_publico: '',
        precio_efectivo: '',
        id_proveedor: '',
        codigo_ean: '',
        sol: false
      });
      setPrecioEfectivoManual(false);
      setModoNuevaMarca(prev => ({ ...prev, normal: false }));
      cargarArmazones();
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    }
  };

  const handleEditarArmazon = async (armazon) => {
    const result = await Swal.fire({
      title: '¿Editar armazón?',
      text: `¿Desea editar el armazón "${armazon.marca} ${armazon.modelo}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, editar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      setArmazonEditando(armazon);
      setModoNuevaMarca(prev => ({ ...prev, edicion: false }));
      setFormEdicion({
        marca: armazon.marca,
        modelo: armazon.modelo,
        color: armazon.color,
        material: armazon.material,
        cantidad: armazon.cantidad.toString(),
        medidas: armazon.medidas || '',
        tipo_bisagra: armazon.tipo_bisagra || '',
        costo: armazon.costo || '',
        precio_publico: armazon.precio_publico || '',
        precio_efectivo: armazon.precio_efectivo ?? calcularPrecioEfectivo(armazon.precio_publico),
        id_proveedor: armazon.id_proveedor || '',
        codigo_ean: armazon.codigo_ean || '',
        sol: armazon.sol || false,
      });
      setPrecioEfectivoManualEdicion(armazon.precio_efectivo != null && String(armazon.precio_efectivo).trim() !== '');
      setEditando(true);
    }
  };

  const handleDuplicarArmazon = (armazon) => {
    setArmazonDuplicando(armazon);
    setModoNuevaMarcaDuplicado(false);
    setFormDuplicado({
      marca: armazon.marca,
      modelo: armazon.modelo,
      color: armazon.color,
      material: armazon.material,
      cantidad: '',  // Cantidad vacía para que el usuario ingrese una nueva
      medidas: armazon.medidas || '',
      tipo_bisagra: armazon.tipo_bisagra || '',
      costo: armazon.costo || '',
      precio_publico: armazon.precio_publico || '',
      precio_efectivo: armazon.precio_efectivo ?? calcularPrecioEfectivo(armazon.precio_publico),
      id_proveedor: armazon.id_proveedor || '',
      codigo_ean: armazon.codigo_ean || '',
      sol: armazon.sol || false,
    });
    setModalDuplicar(true);
  };

  const cerrarModalDuplicar = () => {
    setModalDuplicar(false);
    setArmazonDuplicando(null);
    setModoNuevaMarcaDuplicado(false);
    setFormDuplicado({
      marca: '',
      modelo: '',
      color: '',
      material: '',
      cantidad: '',
      medidas: '',
      tipo_bisagra: '',
      costo: '',
      precio_publico: '',
      precio_efectivo: '',
      id_proveedor: '',
      codigo_ean: '',
      sol: false
    });
  };

  const handleChangeDuplicado = (e) => {
    const { name, value } = e.target;
    setFormDuplicado(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'precio_publico' && !Number.isNaN(value) && value !== '') {
        updated.precio_efectivo = calcularPrecioEfectivo(value);
      }
      return updated;
    });
  };

  const handleSubmitDuplicado = async (e) => {
    e.preventDefault();
    if (!formDuplicado.marca || !formDuplicado.modelo || !formDuplicado.color || !formDuplicado.material || !formDuplicado.cantidad) {
      Swal.fire('Error', 'Por favor complete los campos obligatorios.', 'error');
      return;
    }
    
    setGuardandoDuplicado(true);
    try {
      const marcaFinal = await asegurarMarcaEnCatalogo(formDuplicado.marca);
      const colorFinal = await asegurarColorEnCatalogo(formDuplicado.color);
      await agregarArmazon({
        marca: marcaFinal,
        modelo: formDuplicado.modelo,
        color: colorFinal,
        material: formDuplicado.material,
        cantidad: parseInt(formDuplicado.cantidad),
        medidas: formDuplicado.medidas || '',
        tipo_bisagra: formDuplicado.tipo_bisagra || '',
        costo: formDuplicado.costo ? parseFloat(formDuplicado.costo) : null,
        precio_publico: formDuplicado.precio_publico ? parseFloat(formDuplicado.precio_publico) : null,
        precio_efectivo: formDuplicado.precio_efectivo ? parseFloat(formDuplicado.precio_efectivo) : null,
        id_proveedor: formDuplicado.id_proveedor || null,
        codigo_ean: formDuplicado.codigo_ean || '',
      });
      
      Swal.fire('¡Duplicado!', 'El armazón ha sido duplicado exitosamente.', 'success');
      cerrarModalDuplicar();
      cargarArmazones();
    } catch (error) {
      console.error('Error al duplicar:', error);
      Swal.fire('Error', 'No se pudo duplicar el armazón.', 'error');
    } finally {
      setGuardandoDuplicado(false);
    }
  };

  const toggleSeleccionarTodos = () => {
    if (seleccionados.size === armazonesFiltrados.length && armazonesFiltrados.length > 0) {
      setSeleccionados(new Set());
    } else {
      setSeleccionados(new Set(armazonesFiltrados.map((a) => a.id)));
    }
  };

  const toggleSeleccionarArmazon = (id) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleEliminarSeleccionados = async () => {
    const ids = Array.from(seleccionados);
    if (ids.length === 0) return;

    const { value: confirmacionTexto } = await Swal.fire({
      title: '¿Eliminar armazones seleccionados?',
      html: `
        <div class="text-left text-sm text-slate-700 space-y-2">
          <p>Estás a punto de eliminar <strong>${ids.length}</strong> armazón(es) del stock de forma permanente.</p>
          <p class="text-red-600 font-semibold">Esta acción no se puede deshacer.</p>
          <p class="text-slate-500 text-xs">Escribí <strong>ELIMINAR</strong> a continuación para confirmar:</p>
        </div>
      `,
      input: 'text',
      inputPlaceholder: 'ELIMINAR',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar lote',
      cancelButtonText: 'Cancelar',
      inputValidator: (value) => {
        if (!value || value.trim().toUpperCase() !== 'ELIMINAR') {
          return 'Debes escribir exactamente ELIMINAR para proceder.';
        }
        return null;
      }
    });

    if (!confirmacionTexto) return;

    setEliminandoLote(true);
    try {
      let eliminadosCount = 0;
      let fallidosCount = 0;

      for (const id of ids) {
        try {
          await eliminarArmazon(id);
          eliminadosCount += 1;
        } catch (_err) {
          fallidosCount += 1;
        }
      }

      setSeleccionados(new Set());
      setModoSeleccionMultiple(false);
      await cargarArmazones();

      if (fallidosCount === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Lote eliminado',
          text: `Se eliminaron exitosamente ${eliminadosCount} armazones.`,
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'Eliminación parcial',
          text: `Se eliminaron ${eliminadosCount} armazones. Fallaron ${fallidosCount}.`
        });
      }
    } catch (e) {
      Swal.fire('Error', e.message || 'Ocurrió un error al eliminar el lote.', 'error');
    } finally {
      setEliminandoLote(false);
    }
  };

  const handleEliminarArmazon = async (armazon) => {
    const result = await Swal.fire({
      title: '¿Está seguro?',
      text: `¿Desea eliminar el armazón "${armazon.marca} ${armazon.modelo}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await eliminarArmazon(armazon.id);
        Swal.fire('¡Eliminado!', 'El armazón ha sido eliminado.', 'success');
        cargarArmazones();
      } catch (e) {
        Swal.fire('Error', e.message, 'error');
      }
    }
  };

  const handleChangeEdicion = e => {
    const { name, value } = e.target;
    setFormEdicion((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (name === 'precio_publico' && !precioEfectivoManualEdicion) {
        next.precio_efectivo = calcularPrecioEfectivo(value);
      }
      if (name === 'precio_publico' && String(value || '').trim() === '' && !precioEfectivoManualEdicion) {
        next.precio_efectivo = '';
      }

      return next;
    });

    if (name === 'precio_efectivo') {
      setPrecioEfectivoManualEdicion(String(value || '').trim() !== '');
    }
  };

  const handleSubmitEdicion = async e => {
    e.preventDefault();
    try {
      const marcaFinal = await asegurarMarcaEnCatalogo(formEdicion.marca);
      const colorFinal = await asegurarColorEnCatalogo(formEdicion.color);

      // Validar que cantidad sea un número
      const cantidad = parseInt(formEdicion.cantidad);
      if (isNaN(cantidad) || cantidad < 0) {
        Swal.fire('Error', 'La cantidad debe ser un número válido mayor o igual a 0', 'error');
        return;
      }

      const armazonData = {
        ...formEdicion,
        marca: marcaFinal,
        color: colorFinal,
        cantidad: cantidad,
        costo: formEdicion.costo ? parseFloat(formEdicion.costo) : null,
        precio_publico: formEdicion.precio_publico ? parseFloat(formEdicion.precio_publico) : null,
        precio_efectivo: formEdicion.precio_efectivo ? parseFloat(formEdicion.precio_efectivo) : null,
        id_proveedor: formEdicion.id_proveedor ? parseInt(formEdicion.id_proveedor) : null,
      };

      await editarArmazon(armazonEditando.id, armazonData);
      Swal.fire('¡Armazón actualizado!', '', 'success');
      setEditando(false);
      setArmazonEditando(null);
      setPrecioEfectivoManualEdicion(false);
      setModoNuevaMarca(prev => ({ ...prev, edicion: false }));
      cargarArmazones();
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    }
  };

  const cerrarModalEdicion = () => {
    setEditando(false);
    setArmazonEditando(null);
    setModoNuevaMarca(prev => ({ ...prev, edicion: false }));
    setFormEdicion({
      marca: '',
      modelo: '',
      color: '',
      material: '',
      cantidad: '',
      medidas: '',
      tipo_bisagra: '',
      costo: '',
      precio_publico: '',
      precio_efectivo: '',
      id_proveedor: '',
      codigo_ean: '',
      sol: false
    });
    setPrecioEfectivoManualEdicion(false);
  };

  // ── Carga masiva ────────────────────────────────────────────────────────
  const handleFilaMasivaChange = (idx, e) => {
    const { name, value } = e.target;
    setFilasMasivas(prev => prev.map((f, i) => i === idx ? { ...f, [name]: value } : f));
  };

  const agregarFila = () => setFilasMasivas(prev => [...prev, { ...FILA_VACIA }]);

  const agregarFilaMasiva = () => {
    setFilasMasivas(prev => [...prev, { ...FILA_VACIA }]);
    setFilasMasivasExpandida({});
  };

  const eliminarFila = (idx) => {
    if (filasMasivas.length === 1) return;
    setFilasMasivas(prev => prev.filter((_, i) => i !== idx));
    setFilasMasivasExpandida({});
  };

  const duplicarFila = (idx) =>
    setFilasMasivas(prev => {
      const copia = [...prev];
      copia.splice(idx + 1, 0, { ...prev[idx] });
      return copia;
    });

  const duplicarFilaMasiva = (idx) => {
    duplicarFila(idx);
    setFilasMasivasExpandida({});
  };

  const toggleFilaMasivaExpandida = (idx) => {
    setFilasMasivasExpandida(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  // ─── Handlers genéricos para catálogos de armazones ──────────────────────────────
  const obtenerTipoCatalogoActual = () => {
    if (catalogoArmazonActivo === 'marcas') return 'marcas_armazones';
    if (catalogoArmazonActivo === 'materiales') return 'material_armazon';
    if (catalogoArmazonActivo === 'colores') return 'color_armazon';
    return null;
  };

  const handleCrearCatalogoArmazon = async () => {
    const nombre = String(nuevoCatalogoArmazonNombre || '').trim();
    if (!nombre) return;
    if (catalogoArmazonActivo === 'proveedores') {
      Swal.fire('Información', 'Los proveedores se gestionan desde la solapa "Proveedores" usando el formulario.', 'info');
      return;
    }
    setGuardandoCatalogoArmazon(true);
    try {
      const tipo = obtenerTipoCatalogoActual();
      if (tipo === 'marcas_armazones') {
        await crearCatalogoCristalesItem(tipo, nombre);
      } else {
        await crearCatalogoArmazonItem(tipo, nombre);
      }
      await cargarCatalogoMarcasArmazones();
      await cargarCatalogosArmazones();
      setNuevoCatalogoArmazonNombre('');
      const etiqueta = catalogoArmazonActivo.slice(0, -1); // Singularizar
      Swal.fire({ icon: 'success', title: `${etiqueta.charAt(0).toUpperCase() + etiqueta.slice(1)} creado/a`, toast: true, position: 'top-end', showConfirmButton: false, timer: 1400 });
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setGuardandoCatalogoArmazon(false);
    }
  };

  const handleRenombrarCatalogoArmazon = async (item) => {
    const { value: nuevoNombre } = await Swal.fire({
      title: `Renombrar ${catalogoArmazonActivo.slice(0, -1)}`,
      input: 'text',
      inputValue: item.nombre,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      inputValidator: (v) => (!String(v || '').trim() ? 'Ingresá un nombre' : null),
    });
    if (!nuevoNombre) return;
    try {
      const tipo = obtenerTipoCatalogoActual();
      if (tipo === 'marcas_armazones') {
        await actualizarCatalogoCristalesItem(tipo, item.id, { nombre: String(nuevoNombre).trim() });
      } else {
        await actualizarCatalogoArmazonItem(tipo, item.id, { nombre: String(nuevoNombre).trim() });
      }
      await cargarCatalogoMarcasArmazones();
      await cargarCatalogosArmazones();
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    }
  };

  const handleToggleActivoCatalogoArmazon = async (item) => {
    try {
      const tipo = obtenerTipoCatalogoActual();
      if (tipo === 'marcas_armazones') {
        if (item.activo) await desactivarCatalogoCristalesItem(tipo, item.id);
        else await actualizarCatalogoCristalesItem(tipo, item.id, { activo: true });
      } else {
        if (item.activo) await desactivarCatalogoArmazonItem(tipo, item.id);
        else await actualizarCatalogoArmazonItem(tipo, item.id, { activo: true });
      }
      await cargarCatalogoMarcasArmazones();
      await cargarCatalogosArmazones();
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    }
  };

  const handleEliminarCatalogoArmazon = async (item) => {
    const result = await Swal.fire({
      title: `Eliminar ${catalogoArmazonActivo.slice(0, -1)}`,
      text: `Se eliminará definitivamente "${item.nombre}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;
    try {
      const tipo = obtenerTipoCatalogoActual();
      if (tipo === 'marcas_armazones') {
        await eliminarCatalogoCristalesItem(tipo, item.id);
      } else {
        await eliminarCatalogoArmazonItem(tipo, item.id);
      }
      await cargarCatalogoMarcasArmazones();
      await cargarCatalogosArmazones();
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    }
  };

  const handleGuardarMasivo = async () => {
    if (!marcaMasiva.trim()) {
      Swal.fire('Falta la marca', 'Ingresá el nombre de la marca antes de guardar.', 'warning');
      return;
    }
    const marcaFinal = await asegurarMarcaEnCatalogo(marcaMasiva);
    const filaValidas = filasMasivas.filter(f => f.modelo.trim() || f.color.trim());
    if (filaValidas.length === 0) {
      Swal.fire('Sin datos', 'Completá al menos modelo o color en una fila.', 'warning');
      return;
    }
    setGuardandoMasivo(true);
    let guardados = 0;
    const errores = [];
    for (const fila of filaValidas) {
      const cantidad = parseInt(fila.cantidad);
      try {
        const colorFinal = await asegurarColorEnCatalogo(fila.color);
        await agregarArmazon({
          marca: marcaFinal,
          modelo: fila.modelo,
          color: colorFinal,
          material: fila.material,
          cantidad: isNaN(cantidad) ? 0 : cantidad,
          medidas: fila.medidas,
          tipo_bisagra: fila.tipo_bisagra,
          costo: fila.costo ? parseFloat(fila.costo) : null,
          precio_publico: fila.precio_publico ? parseFloat(fila.precio_publico) : null,
          precio_efectivo: fila.precio_efectivo ? parseFloat(fila.precio_efectivo) : null,
          id_proveedor: fila.id_proveedor ? parseInt(fila.id_proveedor) : null,
        });
        guardados++;
      } catch (e) {
        errores.push(`${fila.modelo || fila.color}: ${e.message}`);
      }
    }
    setGuardandoMasivo(false);
    if (errores.length === 0) {
      Swal.fire('¡Listo!', `Se agregaron ${guardados} armazón(es) de la marca "${marcaFinal}".`, 'success');
      setMarcaMasiva('');
      setFilasMasivas([{ ...FILA_VACIA }]);
      setModoMasivo(false);
      setModoNuevaMarca(prev => ({ ...prev, masiva: false }));
    } else {
      Swal.fire('Parcialmente guardado', `${guardados} guardados. Errores:\n${errores.join('\n')}`, 'warning');
    }
    cargarArmazones();
  };

  const getEstadoStock = (cantidad) => {
    if (cantidad === 0) {
      return { text: '  ', color: 'text-xs font-medium text-red-700 bg-red-200 px-2 py-1 rounded-full' };
    } else if (cantidad <= 3) {
      return { text: '  ', color: 'text-xs font-medium text-yellow-600 bg-yellow-200 px-2 py-1 rounded-full' };
    } else {
      return { text: '  ', color: 'text-xs font-medium text-green-600 bg-green-200 px-2 py-1 rounded-full' };
    }
  };

  const handleAjustarStockRapido = async (armazon, delta) => {
    const cantidadActual = parseInt(armazon.cantidad, 10) || 0;
    const nuevaCantidad = Math.max(0, cantidadActual + delta);
    if (nuevaCantidad === cantidadActual) return;

    setActualizandoStockId(armazon.id);
    try {
      await editarArmazon(armazon.id, {
        marca: armazon.marca,
        modelo: armazon.modelo,
        color: armazon.color,
        material: armazon.material,
        cantidad: nuevaCantidad,
        medidas: armazon.medidas || null,
        tipo_bisagra: armazon.tipo_bisagra || null,
        costo: armazon.costo != null ? parseFloat(armazon.costo) : null,
        precio_publico: armazon.precio_publico != null ? parseFloat(armazon.precio_publico) : null,
        precio_efectivo: armazon.precio_efectivo != null ? parseFloat(armazon.precio_efectivo) : null,
      });

      setArmazones((prev) => prev.map((item) => (
        item.id === armazon.id ? { ...item, cantidad: nuevaCantidad } : item
      )));
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setActualizandoStockId(null);
    }
  };

  // Actualizar armazones cuyos proveedores ya estén asignados
  const handleActualizarArmazonesConProveedor = async () => {
    const result = await Swal.fire({
      title: '¿Actualizar armazones con proveedores asignados?',
      text: 'Se actualizarán todos los armazones cuyas marcas ya tengan un proveedor configurado.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, actualizar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    let actualizados = 0;
    const errores = [];

    try {
      for (const armazon of armazones) {
        const idProveedorPorMarca = obtenerProveedorPorMarca(armazon.marca);
        
        // Solo actualizar si el proveedor está asignado pero el armazon no lo tiene
        if (idProveedorPorMarca && (!armazon.id_proveedor || armazon.id_proveedor !== idProveedorPorMarca)) {
          try {
            await editarArmazon(armazon.id, {
              marca: armazon.marca,
              modelo: armazon.modelo,
              color: armazon.color,
              material: armazon.material,
              cantidad: armazon.cantidad,
              medidas: armazon.medidas || null,
              tipo_bisagra: armazon.tipo_bisagra || null,
              costo: armazon.costo != null ? parseFloat(armazon.costo) : null,
              precio_publico: armazon.precio_publico != null ? parseFloat(armazon.precio_publico) : null,
              precio_efectivo: armazon.precio_efectivo != null ? parseFloat(armazon.precio_efectivo) : null,
              id_proveedor: idProveedorPorMarca,
            });
            actualizados++;
          } catch (e) {
            errores.push(`${armazon.marca} ${armazon.modelo}: ${e.message}`);
          }
        }
      }

      await cargarArmazones();

      if (errores.length === 0) {
        Swal.fire('¡Listo!', `Se actualizaron ${actualizados} armazón(es) con sus proveedores.`, 'success');
      } else {
        Swal.fire('Parcialmente actualizado', `${actualizados} actualizados. Errores:\n${errores.join('\n')}`, 'warning');
      }
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <div className="mx-auto w-full max-w-[96rem]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-2xl font-bold">Stock de Armazones</h2>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setModoNuevaMarca(prev => ({ ...prev, normal: false }));
                setModalAgregarArmazon(true);
              }}
              className="px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2 shadow-sm transition-colors cursor-pointer"
            >
              <PlusIcon className="h-4 w-4" />
              Agregar Armazón
            </button>
            <button
              type="button"
              onClick={() => setModalPdf(true)}
              className="px-4 py-2 rounded-md text-sm font-medium border border-green-300 text-green-700 bg-white hover:bg-green-50 flex items-center gap-2"
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
              Cargar desde PDF
            </button>
            <button
              type="button"
              onClick={() => {
                setCatalogoArmazonBusqueda('');
                setNuevoCatalogoArmazonNombre('');
                setCatalogoArmazonActivo('marcas');
                setModalCatalogosArmazones(true);
              }}
              className="px-4 py-2 rounded-md text-sm font-medium border border-blue-300 text-blue-700 bg-white hover:bg-blue-50 flex items-center gap-2"
            >
              <BeakerIcon className="h-4 w-4" />
              Herramientas de armazones
            </button>
            <button
              type="button"
              onClick={() => {
                setModoMasivo(v => !v);
                setMarcaMasiva('');
                setFilasMasivas([{ ...FILA_VACIA }]);
                setFilasMasivasExpandida({});
                setModoNuevaMarca(prev => ({ ...prev, masiva: false }));
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${modoMasivo ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700' : 'bg-white text-indigo-600 border-indigo-400 hover:bg-indigo-50'}`}
            >
              {modoMasivo ? '✕ Cancelar carga masiva' : '+ Carga masiva por marca'}
            </button>
          </div>
        </div>

        {/* ── Panel de carga masiva ── */}
        {modoMasivo && (
          <div className="bg-white shadow rounded-lg p-4 sm:p-5 mb-6 border border-indigo-200">
            <h3 className="text-sm sm:text-base font-semibold text-indigo-700 mb-4">Carga masiva — ingresá la marca una vez y completá cada armazón</h3>

            {/* Marca global */}
            <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-4 mb-5">
              <div className="w-full md:w-64">
                <label className="block text-sm font-medium mb-1">Marca <span className="text-red-500">*</span></label>
                {!modoNuevaMarca.masiva ? (
                  <select
                    value={marcaMasiva}
                    onChange={e => {
                      if (e.target.value === '__nuevo__') {
                        setModoNuevaMarca(prev => ({ ...prev, masiva: true }));
                        setMarcaMasiva('');
                      } else {
                        setMarcaMasiva(e.target.value);
                        // Auto-asignar proveedor si la marca ya tiene uno
                        const idProveedor = obtenerProveedorPorMarca(e.target.value);
                        if (idProveedor) {
                          setFilasMasivas(prev => 
                            prev.map(f => ({ ...f, id_proveedor: idProveedor.toString() }))
                          );
                        }
                      }
                    }}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Seleccionar marca...</option>
                    {optionLegado(marcaMasiva, 'leg-masiva')}
                    {marcasArmazonesActivas.map((nombre) => <option key={`marca-masiva-${nombre}`} value={nombre}>{nombre}</option>)}
                    <option value="__nuevo__">+ Crear nueva marca...</option>
                  </select>
                ) : (
                  <div className="space-y-1">
                    <input
                      type="text"
                      value={marcaMasiva}
                      onChange={e => setMarcaMasiva(e.target.value)}
                      placeholder="Ej: Oakley"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => setModoNuevaMarca(prev => ({ ...prev, masiva: false }))}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      Usar catálogo existente
                    </button>
                  </div>
                )}
              </div>
              <span className="text-xs sm:text-sm text-gray-500 md:pb-2">Esta marca se aplicará a todos los armazones de abajo.</span>
            </div>

            {/* Tabla de filas (desktop) */}
            <div className="hidden lg:block overflow-x-auto mb-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-indigo-50 text-indigo-700 text-xs uppercase">
                    <th className="px-2 py-2 text-left font-semibold">#</th>
                    <th className="px-2 py-2 text-left font-semibold">Modelo</th>
                    <th className="px-2 py-2 text-left font-semibold">Color</th>
                    <th className="px-2 py-2 text-left font-semibold">Material</th>
                    <th className="px-2 py-2 text-left font-semibold">Cant.</th>
                    <th className="px-2 py-2 text-left font-semibold">Medidas</th>
                    <th className="px-2 py-2 text-left font-semibold">Código EAN</th>
                    <th className="px-2 py-2 text-left font-semibold">Bisagra</th>
                    <th className="px-2 py-2 text-left font-semibold">Costo</th>
                    <th className="px-2 py-2 text-left font-semibold">Precio Público</th>
                    <th className="px-2 py-2 text-left font-semibold">Precio Efectivo</th>
                    <th className="px-2 py-2 text-left font-semibold">Proveedor</th>
                    <th className="px-2 py-2 text-center font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filasMasivas.map((fila, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-indigo-50/30'}>
                      <td className="px-2 py-1 text-gray-400 text-xs">{idx + 1}</td>
                      <td className="px-1 py-1">
                        <input type="text" name="modelo" value={fila.modelo} onChange={e => handleFilaMasivaChange(idx, e)}
                          placeholder="Modelo" className="w-full p-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="text" name="color" value={fila.color} onChange={e => handleFilaMasivaChange(idx, e)}
                          placeholder="Color" list={`coloresMasiva${idx}`} className="w-full p-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                        <datalist id={`coloresMasiva${idx}`}>
                          {(coloresArmazonesCatalogo || [])
                            .filter((i) => i.activo)
                            .map((i) => (
                              <option key={i.id} value={i.nombre} />
                            ))
                          }
                        </datalist>
                      </td>
                      <td className="px-1 py-1">
                        <input type="text" name="material" value={fila.material} onChange={e => handleFilaMasivaChange(idx, e)}
                          placeholder="Material" list={`materialesMasiva${idx}`} className="w-full p-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                        <datalist id={`materialesMasiva${idx}`}>
                          {(materialesArmazonesCatalogo || [])
                            .filter((i) => i.activo)
                            .map((i) => (
                              <option key={i.id} value={i.nombre} />
                            ))
                          }
                        </datalist>
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" name="cantidad" value={fila.cantidad} onChange={e => handleFilaMasivaChange(idx, e)}
                          min="0" placeholder="0" className="w-16 p-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="text" name="medidas" value={fila.medidas} onChange={e => handleFilaMasivaChange(idx, e)}
                          placeholder="54-18-145" className="w-28 p-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="text" name="codigo_ean" value={fila.codigo_ean} onChange={e => handleFilaMasivaChange(idx, e)}
                          placeholder="EAN" className="w-32 p-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                      </td>
                      <td className="px-1 py-1">
                        <select name="tipo_bisagra" value={fila.tipo_bisagra} onChange={e => handleFilaMasivaChange(idx, e)}
                          className="w-full p-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400">
                          <option value="">—</option>
                          <option>Clasica</option><option>Flex</option><option>Triple</option><option>Silicona</option><option>Otros</option>
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" name="costo" value={fila.costo} onChange={e => handleFilaMasivaChange(idx, e)}
                          min="0" step="0.01" placeholder="0" className="w-24 p-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" name="precio_publico" value={fila.precio_publico} onChange={e => handleFilaMasivaChange(idx, e)}
                          min="0" step="0.01" placeholder="0" className="w-28 p-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" name="precio_efectivo" value={fila.precio_efectivo} onChange={e => handleFilaMasivaChange(idx, e)}
                          min="0" step="0.01" placeholder="0" className="w-28 p-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                      </td>
                      <td className="px-1 py-1">
                        {fila.id_proveedor ? (
                          <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded whitespace-nowrap">
                            {proveedores.find(p => p.id === parseInt(fila.id_proveedor))?.nombre || 'Proveedor'}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Sin asignar</span>
                        )}
                      </td>
                      <td className="px-1 py-1 text-center whitespace-nowrap">
                        <button type="button" onClick={() => duplicarFila(idx)} title="Duplicar fila"
                          className="px-1.5 py-1 text-blue-500 hover:text-blue-700 text-xs">⧉</button>
                        <button type="button" onClick={() => eliminarFila(idx)} title="Eliminar fila" disabled={filasMasivas.length === 1}
                          className="px-1.5 py-1 text-red-400 hover:text-red-600 text-xs disabled:opacity-30">✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Filas tipo card (mobile/tablet) */}
            <div className="lg:hidden space-y-3 mb-4">
              {filasMasivas.map((fila, idx) => (
                <div key={idx} className="border border-indigo-100 rounded-lg p-3 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-indigo-700">Fila #{idx + 1}</span>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => duplicarFilaMasiva(idx)} title="Duplicar fila"
                        className="px-2 py-1 text-blue-600 hover:text-blue-800 text-xs border border-blue-200 rounded">Duplicar</button>
                      <button type="button" onClick={() => eliminarFila(idx)} title="Eliminar fila" disabled={filasMasivas.length === 1}
                        className="px-2 py-1 text-red-500 hover:text-red-700 text-xs border border-red-200 rounded disabled:opacity-30">Eliminar</button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input type="text" name="modelo" value={fila.modelo} onChange={e => handleFilaMasivaChange(idx, e)}
                      placeholder="Modelo" className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                    <input type="text" name="color" value={fila.color} onChange={e => handleFilaMasivaChange(idx, e)}
                      placeholder="Color" list={`coloresMobil${idx}`} className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                    <datalist id={`coloresMobil${idx}`}>
                      {(coloresArmazonesCatalogo || [])
                        .filter((i) => i.activo)
                        .map((i) => (
                          <option key={i.id} value={i.nombre} />
                        ))
                      }
                    </datalist>
                    <input type="number" name="cantidad" value={fila.cantidad} onChange={e => handleFilaMasivaChange(idx, e)}
                      min="0" placeholder="Cantidad" className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                  </div>
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => toggleFilaMasivaExpandida(idx)}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      {filasMasivasExpandida[idx] ? 'Menos campos' : 'Más campos'}
                    </button>
                  </div>

                  {filasMasivasExpandida[idx] && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      <input type="text" name="material" value={fila.material} onChange={e => handleFilaMasivaChange(idx, e)}
                        placeholder="Material" list={`materialesMobil${idx}`} className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                      <datalist id={`materialesMobil${idx}`}>
                        {(materialesArmazonesCatalogo || [])
                          .filter((i) => i.activo)
                          .map((i) => (
                            <option key={i.id} value={i.nombre} />
                          ))
                        }
                      </datalist>
                      <input type="text" name="medidas" value={fila.medidas} onChange={e => handleFilaMasivaChange(idx, e)}
                        placeholder="Medidas (54-18-145)" className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                      <input type="text" name="codigo_ean" value={fila.codigo_ean} onChange={e => handleFilaMasivaChange(idx, e)}
                        placeholder="Código EAN" className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                      <select name="tipo_bisagra" value={fila.tipo_bisagra} onChange={e => handleFilaMasivaChange(idx, e)}
                        className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400">
                        <option value="">Bisagra</option>
                        <option>Clasica</option><option>Flex</option><option>Triple</option><option>Silicona</option><option>Otros</option>
                      </select>
                      <input type="number" name="costo" value={fila.costo} onChange={e => handleFilaMasivaChange(idx, e)}
                        min="0" step="0.01" placeholder="Costo" className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                      <input type="number" name="precio_publico" value={fila.precio_publico} onChange={e => handleFilaMasivaChange(idx, e)}
                        min="0" step="0.01" placeholder="Precio público" className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                      <input type="number" name="precio_efectivo" value={fila.precio_efectivo} onChange={e => handleFilaMasivaChange(idx, e)}
                        min="0" step="0.01" placeholder="Precio efectivo" className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400" />
                      <select name="id_proveedor" value={fila.id_proveedor} onChange={e => handleFilaMasivaChange(idx, e)}
                        className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-400">
                        <option value="">Proveedor (opcional)</option>
                        {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <button type="button" onClick={agregarFilaMasiva}
                className="px-3 py-1.5 border border-indigo-400 text-indigo-600 text-sm rounded hover:bg-indigo-50">
                + Agregar fila
              </button>
              <button type="button" onClick={handleGuardarMasivo} disabled={guardandoMasivo}
                className="px-5 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-60">
                {guardandoMasivo ? 'Guardando...' : `Guardar ${filasMasivas.filter(f => f.modelo.trim() || f.color.trim()).length || filasMasivas.length} armazón(es)`}
              </button>
              <span className="text-xs text-gray-400">Podés duplicar una fila para copiar sus valores como base.</span>
            </div>
          </div>
        )}

        

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex flex-col gap-3 flex-1">
            <h3 className="text-lg font-semibold">Lista de Armazones</h3>
            <div className="flex gap-1 border-b border-gray-200">
              <button
                onClick={() => setSolapaStock('todos')}
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                  solapaStock === 'todos'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => setSolapaStock('con-stock')}
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                  solapaStock === 'con-stock'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Con Stock
              </button>
              <button
                onClick={() => setSolapaStock('sin-stock')}
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                  solapaStock === 'sin-stock'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Sin Stock
              </button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <input
              type="text"
              value={busquedaArmazones}
              onChange={(e) => setBusquedaArmazones(e.target.value)}
              placeholder="Buscar por marca, modelo, color, material..."
              className="w-full sm:w-80 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={tamanoPaginaArmazones}
              onChange={(e) => setTamanoPaginaArmazones(parseInt(e.target.value, 10))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value={8}>8 / página</option>
              <option value={12}>12 / página</option>
              <option value={20}>20 / página</option>
              <option value={40}>40 / página</option>
            </select>
            <button
              type="button"
              onClick={() => {
                if (modoSeleccionMultiple) {
                  setSeleccionados(new Set());
                  setModoSeleccionMultiple(false);
                } else {
                  setModoSeleccionMultiple(true);
                }
              }}
              className={`inline-flex items-center justify-center gap-1.5 px-3.5 py-2 border rounded-md text-sm font-medium transition-all shadow-sm cursor-pointer whitespace-nowrap ${
                modoSeleccionMultiple
                  ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
              title={modoSeleccionMultiple ? 'Cancelar selección múltiple' : 'Activar selección múltiple para borrar varios'}
            >
              {modoSeleccionMultiple ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                  <span>Cancelar selección</span>
                </>
              ) : (
                <>
                  <ListBulletIcon className="w-4 h-4 text-gray-500" />
                  <span>Seleccionar varios</span>
                </>
              )}
            </button>
          </div>
        </div>
        {loading ? (
          <div className="text-center py-4">Cargando...</div>
        ) : armazonesFiltrados.length === 0 ? (
          <div className="text-center py-8 bg-white rounded shadow">
            <p className="text-gray-500">No se encontraron armazones para la búsqueda actual</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {seleccionados.size > 0 && (
              <div className="bg-red-50 border-b border-red-200 px-6 py-3 flex items-center justify-between gap-3 animate-fadeIn">
                <div className="flex items-center gap-2 text-sm text-red-900 font-medium">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />
                  <span>{seleccionados.size} armazón(es) seleccionado(s)</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSeleccionados(new Set())}
                    className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 rounded-lg transition-colors cursor-pointer"
                  >
                    Deseleccionar todos
                  </button>
                  <button
                    type="button"
                    onClick={handleEliminarSeleccionados}
                    disabled={eliminandoLote}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-sm transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <TrashIcon className="w-4 h-4" />
                    <span>{eliminandoLote ? 'Eliminando...' : `Eliminar seleccionados (${seleccionados.size})`}</span>
                  </button>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-[980px] w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {modoSeleccionMultiple && (
                      <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                        <input
                          type="checkbox"
                          checked={armazonesFiltrados.length > 0 && seleccionados.size === armazonesFiltrados.length}
                          onChange={toggleSeleccionarTodos}
                          title="Seleccionar / deseleccionar todos los armazones"
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                        />
                      </th>
                    )}
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Marca
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Modelo
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Color
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Material
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Tipo
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Stock
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Medidas
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Bisagra
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Costo
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Precio Público
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Proveedor
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Fecha Ingreso
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      ML
                    </th>
                    <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {armazonesPaginados.map((armazon) => {
                    const estadoStock = getEstadoStock(armazon.cantidad);
                    const estaSeleccionado = seleccionados.has(armazon.id);
                    return (
                      <tr key={armazon.id} className={`hover:bg-gray-50 ${estaSeleccionado ? 'bg-blue-50/50' : ''}`}>
                        {modoSeleccionMultiple && (
                          <td className="px-3 py-2.5 whitespace-nowrap text-center">
                            <input
                              type="checkbox"
                              checked={estaSeleccionado}
                              onChange={() => toggleSeleccionarArmazon(armazon.id)}
                              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                            />
                          </td>
                        )}
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {armazon.marca}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-800">
                          {armazon.modelo}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700">
                          {armazon.color}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600">
                          {armazon.material || '-'}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm">
                          <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${armazon.sol ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-blue-100 text-blue-800 border border-blue-200'}`}>
                            {armazon.sol ? 'Sol' : 'Receta'}
                          </span>
                        </td>
                        <td
                          className="px-3 py-2.5 whitespace-nowrap"
                          onMouseEnter={() => setHoverStockId(armazon.id)}
                          onMouseLeave={() => setHoverStockId(null)}
                        >
                          <div className="flex items-center">
                            <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full text-black ${estadoStock.color}`}>
                              {armazon.cantidad} u.
                            </span>
                            <button
                              type="button"
                              onClick={() => setStockTouchId((prev) => (prev === armazon.id ? null : armazon.id))}
                              className="ml-1.5 inline-flex sm:hidden px-1.5 py-0.5 text-[11px] rounded border border-gray-300 text-gray-700"
                              title="Ajustar stock"
                            >
                              ±
                            </button>
                            {(hoverStockId === armazon.id || stockTouchId === armazon.id) && (
                              <div className="ml-1.5 flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleAjustarStockRapido(armazon, -1)}
                                  disabled={actualizandoStockId === armazon.id || parseInt(armazon.cantidad, 10) <= 0}
                                  className="px-1.5 py-0.5 text-xs rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-40"
                                  title="Restar 1"
                                >
                                  -1
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleAjustarStockRapido(armazon, +1)}
                                  disabled={actualizandoStockId === armazon.id}
                                  className="px-1.5 py-0.5 text-xs rounded border border-green-200 text-green-700 hover:bg-green-50 disabled:opacity-40"
                                  title="Sumar 1"
                                >
                                  +1
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-xs text-gray-500">
                          {armazon.medidas || '-'}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-xs text-gray-500">
                          {armazon.tipo_bisagra || '-'}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-800">
                          {armazon.costo ? `$${parseFloat(armazon.costo).toFixed(2)}` : '-'}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm font-medium text-gray-900">
                          {armazon.precio_publico ? `$${parseFloat(armazon.precio_publico).toFixed(2)}` : '-'}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm">
                          {armazon.nombre_proveedor ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                              {armazon.nombre_proveedor}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-xs text-gray-500">
                          {armazon.fecha_ingreso ? new Date(armazon.fecha_ingreso).toLocaleDateString('es-ES') : '-'}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-sm">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            armazon.en_ml 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'text-gray-400'
                          }`}>
                            {armazon.en_ml ? 'Publicado' : '-'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-center">
                          <div className="inline-flex items-center justify-center gap-1.5">
                            <button
                              onClick={() => handleEditarArmazon(armazon)}
                              className="p-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 transition-colors shadow-xs cursor-pointer"
                              title="Editar armazón"
                            >
                              <PencilIcon className="w-4 h-4" /> 
                            </button>
                            <button
                              onClick={() => handleDuplicarArmazon(armazon)}
                              className="p-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 transition-colors shadow-xs cursor-pointer"
                              title="Duplicar armazón"
                            >
                              <DocumentDuplicateIcon className="w-4 h-4" /> 
                            </button>
                            <button
                              onClick={() => handleEliminarArmazon(armazon)}
                              className="p-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 focus:ring-2 focus:ring-red-500 transition-colors shadow-xs cursor-pointer"
                              title="Eliminar armazón"
                            >
                              <TrashIcon className="w-4 h-4" /> 
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-t bg-gray-50 text-sm">
              <span className="text-gray-600">
                Mostrando {(paginaArmazones - 1) * tamanoPaginaArmazones + 1}
                –{Math.min(paginaArmazones * tamanoPaginaArmazones, armazonesFiltrados.length)} de {armazonesFiltrados.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPaginaArmazones(1)}
                  disabled={paginaArmazones === 1}
                  className="px-2 py-1 border rounded disabled:opacity-40"
                >
                  «
                </button>
                <button
                  type="button"
                  onClick={() => setPaginaArmazones((p) => Math.max(1, p - 1))}
                  disabled={paginaArmazones === 1}
                  className="px-2 py-1 border rounded disabled:opacity-40"
                >
                  ‹
                </button>
                <span className="text-gray-700">{paginaArmazones} / {totalPaginasArmazones}</span>
                <button
                  type="button"
                  onClick={() => setPaginaArmazones((p) => Math.min(totalPaginasArmazones, p + 1))}
                  disabled={paginaArmazones >= totalPaginasArmazones}
                  className="px-2 py-1 border rounded disabled:opacity-40"
                >
                  ›
                </button>
                <button
                  type="button"
                  onClick={() => setPaginaArmazones(totalPaginasArmazones)}
                  disabled={paginaArmazones >= totalPaginasArmazones}
                  className="px-2 py-1 border rounded disabled:opacity-40"
                >
                  »
                </button>
              </div>
            </div>
          </div>
        )}

        
        {/* ── Modal Agregar Armazón ── */}
        {modalAgregarArmazon && (
          <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 overflow-y-auto bg-slate-950/80 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-auto max-h-[90vh] flex flex-col overflow-hidden animate-fadeIn">
              {/* Encabezado del modal */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-slate-50">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
                    <PlusIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Agregar Nuevo Armazón</h3>
                    <p className="text-xs text-gray-500">Ingresá los datos del armazón para agregarlo al inventario</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setModalAgregarArmazon(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                  title="Cerrar modal"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Formulario */}
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="p-6 overflow-y-auto space-y-4 flex-1 max-h-[calc(90vh-140px)]">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Marca *</label>
                      {!modoNuevaMarca.normal ? (
                        <select
                          name="marca"
                          value={form.marca}
                          onChange={e => {
                            if (e.target.value === '__nuevo__') {
                              setModoNuevaMarca(prev => ({ ...prev, normal: true }));
                              setForm(prev => ({ ...prev, marca: '' }));
                            } else {
                              handleChange(e);
                              const idProveedor = obtenerProveedorPorMarca(e.target.value);
                              if (idProveedor) {
                                setForm(prev => ({ ...prev, id_proveedor: idProveedor.toString() }));
                              }
                            }
                          }}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          required
                        >
                          <option value="">Seleccionar marca...</option>
                          {optionLegado(form.marca, 'leg-normal')}
                          {marcasArmazonesActivas.map((nombre) => <option key={`marca-normal-${nombre}`} value={nombre}>{nombre}</option>)}
                          <option value="__nuevo__">+ Crear nueva marca...</option>
                        </select>
                      ) : (
                        <div className="space-y-1">
                          <input
                            type="text"
                            name="marca"
                            value={form.marca}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setModoNuevaMarca(prev => ({ ...prev, normal: false }))}
                            className="text-xs text-blue-600 hover:underline cursor-pointer"
                          >
                            Usar catálogo existente
                          </button>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Modelo *</label>
                      <input
                        type="text"
                        name="modelo"
                        value={form.modelo}
                        onChange={handleChange}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Color *</label>
                      <input
                        type="text"
                        name="color"
                        value={form.color}
                        onChange={handleChange}
                        placeholder="Escribir o seleccionar..."
                        list="coloresArmazonesList"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        required
                      />
                      <datalist id="coloresArmazonesList">
                        {(coloresArmazonesCatalogo || [])
                          .filter((i) => i.activo)
                          .map((i) => (
                            <option key={i.id} value={i.nombre} />
                          ))
                        }
                      </datalist>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Material *</label>
                      <input
                        type="text"
                        name="material"
                        value={form.material}
                        onChange={handleChange}
                        placeholder="Escribir o seleccionar..."
                        list="materialesArmazonesList"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        required
                      />
                      <datalist id="materialesArmazonesList">
                        {(materialesArmazonesCatalogo || [])
                          .filter((i) => i.activo)
                          .map((i) => (
                            <option key={i.id} value={i.nombre} />
                          ))
                        }
                      </datalist>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Cantidad/Stock *</label>
                      <input
                        type="number"
                        name="cantidad"
                        value={form.cantidad}
                        onChange={handleChange}
                        min="0"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Medidas</label>
                      <input
                        type="text"
                        name="medidas"
                        value={form.medidas}
                        onChange={handleChange}
                        placeholder="ej: 54-18-145"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Código EAN</label>
                      <input
                        type="text"
                        name="codigo_ean"
                        value={form.codigo_ean}
                        onChange={handleChange}
                        placeholder="ej: 7890123456789"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Tipo de Bisagra</label>
                      <select
                        name="tipo_bisagra"
                        value={form.tipo_bisagra}
                        onChange={handleChange}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="">Sin especificar</option>
                        <option value="Clasica">Clásica</option>
                        <option value="Flex">Flex</option>
                        <option value="Triple">Triple</option>
                        <option value="Silicona">Silicona</option>
                        <option value="Otros">Otros</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Costo</label>
                      <input
                        type="number"
                        name="costo"
                        value={form.costo}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        placeholder="Costo unitario"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Precio Público</label>
                      <input
                        type="number"
                        name="precio_publico"
                        value={form.precio_publico}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        placeholder="Precio de venta"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">Efectivo se calcula con descuento {descuentoEfectivo}% (editable).</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Precio Efectivo</label>
                      <input
                        type="number"
                        name="precio_efectivo"
                        value={form.precio_efectivo}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        placeholder="Auto por descuento"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Proveedor</label>
                      <div className="flex gap-1.5">
                        <select
                          name="id_proveedor"
                          value={form.id_proveedor}
                          onChange={handleChange}
                          className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        >
                          <option value="">Sin proveedor</option>
                          {proveedores.map((prov) => (
                            <option key={prov.id} value={prov.id}>{prov.nombre}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => handleCrearProveedorRapido(setForm, form.marca)}
                          className="px-2.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md whitespace-nowrap transition-colors"
                          title="Crear nuevo proveedor"
                        >
                          + Nuevo
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                      <input
                        type="checkbox"
                        id="modal_sol"
                        name="sol"
                        checked={form.sol}
                        onChange={(e) => setForm(prev => ({ ...prev, sol: e.target.checked }))}
                        className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      />
                      <label htmlFor="modal_sol" className="text-sm font-medium cursor-pointer">Es Anteojo de Sol</label>
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                      <input
                        type="checkbox"
                        id="modal_en_ml"
                        name="en_ml"
                        checked={form.en_ml}
                        onChange={(e) => setForm(prev => ({ ...prev, en_ml: e.target.checked }))}
                        className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      />
                      <label htmlFor="modal_en_ml" className="text-sm font-medium cursor-pointer">Publicado en ML</label>
                    </div>
                  </div>
                </div>

                {/* Footer del modal */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setModalAgregarArmazon(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm cursor-pointer"
                  >
                    Agregar Armazón
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Carga desde PDF */}
        {modalPdf && (
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm overflow-y-auto h-full w-full z-[1050] flex items-start justify-center px-4 sm:px-8 pt-16 sm:pt-24 pb-8">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-90vh overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b">
                <h3 className="text-lg font-medium text-gray-900">Cargar Factura desde PDF</h3>
                <button
                  onClick={() => {
                    setModalPdf(false);
                    setPdfArmazones([]);
                    setPdfAccesorios([]);
                    setTabModalPdf('armazones');
                    setPdfArchivo(null);
                    setPdfMarca('');
                    setPdfResumen(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Sección 1: Seleccionar PDF */}
                {pdfArmazones.length === 0 && pdfAccesorios.length === 0 ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-md font-bold text-red-700 mb-4">
                        Esta opción es por el momento solo para facturas de CACIC. (Vulk, Rusty, Maria Cher y Jellvet)
                        12/08/2026
                      </label>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Seleccionar archivo PDF de factura
                      </label>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setPdfArchivo(e.target.files?.[0] || null)}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Marca del PDF <span className="text-red-600">*</span>
                        </label>
                        <select
                          value={pdfMarca}
                          onChange={(e) => setPdfMarca(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="">-- Seleccionar marca --</option>
                          {marcasDisponiblesPdf.map((marca) => (
                            <option key={marca} value={marca}>
                              {marca}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          ¿Son anteojos de SOL?
                        </label>
                        <input
                          type="checkbox"
                          checked={pdfSol}
                          onChange={(e) => {
                            setPdfSol(e.target.checked);
                            // Si cambió SOL, recalcular precios de todos con el nuevo multiplicador,
                            // salvo los que digan "B CUT" (nunca se marcan como sol).
                            const updated = pdfArmazones.map((a) => {
                              const solFinal = a.contiene_b_cut ? false : e.target.checked;
                              return {
                                ...a,
                                sol: solFinal,
                                ...calcularPreciosPdf(a.costo, solFinal)
                              };
                            });
                            setPdfArmazones(updated);
                          }}
                          className="rounded border-gray-300"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Proveedor (opcional)
                        </label>
                        <select
                          value={pdfIdProveedor}
                          onChange={(e) => setPdfIdProveedor(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="">-- Seleccionar --</option>
                          {proveedores.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.nombre}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <button
                      onClick={async () => {
                        if (!pdfArchivo) {
                          Swal.fire('Error', 'Selecciona un archivo PDF', 'error');
                          return;
                        }
                        if (!pdfMarca) {
                          Swal.fire('Error', 'Selecciona la marca del PDF', 'error');
                          return;
                        }
                        setPdfCargando(true);
                        try {
                          const resultado = await procesarPdfFactura(pdfArchivo, pdfMarca);
                          
                          // 1. Armazones
                          const armazonesConPrecios = (resultado.armazones_validos || []).map((a, idx) => {
                            const solInicial = a.contiene_b_cut ? false : pdfSol;
                            return {
                              ...a,
                              id_temp: idx,
                              sol: solInicial,
                              ...calcularPreciosPdf(a.costo, solInicial)
                            };
                          });
                          setPdfArmazones(armazonesConPrecios);

                          // 2. Accesorios (THE BAG)
                          const accesoriosConPrecios = (resultado.accesorios_validos || []).map((acc, idx) => {
                            const costoNum = parseFloat(acc.costo) || 0;
                            const pLista = acc.precio_lista || redondearAlMillar(costoNum * 3.1);
                            const pEfectivo = calcularPrecioEfectivo(pLista);
                            return {
                              ...acc,
                              id_temp: idx,
                              precio_lista: pLista,
                              precio_efectivo: pEfectivo
                            };
                          });
                          setPdfAccesorios(accesoriosConPrecios);
                          setTabModalPdf(armazonesConPrecios.length > 0 ? 'armazones' : 'accesorios');

                          setPdfResumen({
                            total_procesadas: resultado.resumen?.total_procesadas || 0,
                            total_validas: resultado.resumen?.total_validas || 0,
                            total_accesorios: resultado.resumen?.total_accesorios || 0,
                            total_ignoradas: resultado.resumen?.total_ignoradas || 0,
                            total_invalidas: resultado.resumen?.total_invalidas || 0
                          });
                        } catch (err) {
                          Swal.fire('Error', err.message, 'error');
                        } finally {
                          setPdfCargando(false);
                        }
                      }}
                      disabled={pdfCargando || !pdfArchivo}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-60 font-medium"
                    >
                      {pdfCargando ? 'Procesando...' : 'Procesar PDF'}
                    </button>
                  </div>
                ) : (
                  // Sección 2: Editar datos
                  <div className="space-y-4">
                    {pdfResumen && (
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-gray-700 space-y-1">
                        <p>
                          <span className="font-semibold">{pdfArmazones.length}</span> armazón(es) listo(s) para revisión
                          {pdfAccesorios.length > 0 && (
                            <> y <span className="font-semibold text-amber-700">{pdfAccesorios.length} accesorio(s)</span> detectados</>
                          )}.
                        </p>
                        <p className="text-xs text-gray-500">
                          Total procesado: {pdfResumen.total_procesadas} líneas.
                          {pdfResumen.total_ignoradas > 0 && ` (${pdfResumen.total_ignoradas} banners/publicidad descartados automáticamente)`}
                          {pdfResumen.total_invalidas > 0 && ` (${pdfResumen.total_invalidas} líneas con error)`}
                        </p>
                      </div>
                    )}

                    {/* Selector de pestañas si hay accesorios */}
                    {pdfAccesorios.length > 0 && (
                      <div className="flex border-b border-gray-200 space-x-2">
                        <button
                          type="button"
                          onClick={() => setTabModalPdf('armazones')}
                          className={`py-2 px-4 text-sm font-medium border-b-2 flex items-center gap-2 ${
                            tabModalPdf === 'armazones'
                              ? 'border-blue-600 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <ArchiveBoxIcon className="w-4 h-4" />
                          Armazones ({pdfArmazones.length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setTabModalPdf('accesorios')}
                          className={`py-2 px-4 text-sm font-medium border-b-2 flex items-center gap-2 ${
                            tabModalPdf === 'accesorios'
                              ? 'border-amber-600 text-amber-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <TagIcon className="w-4 h-4" />
                          Accesorios / Bolsos ({pdfAccesorios.length})
                        </button>
                      </div>
                    )}

                    {/* Tabla de Armazones */}
                    {tabModalPdf === 'armazones' && (
                    <div className="overflow-x-auto border rounded-md">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 border-b">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">Marca</th>
                            <th className="px-3 py-2 text-left font-medium">Modelo</th>
                            <th className="px-3 py-2 text-left font-medium">Color</th>
                            <th className="px-3 py-2 text-center font-medium">Sol</th>
                            <th className="px-3 py-2 text-left font-medium">Cant.</th>
                            <th className="px-3 py-2 text-left font-medium">Costo</th>
                            <th className="px-3 py-2 text-left font-medium">Material</th>
                            <th className="px-3 py-2 text-left font-medium">Medidas</th>
                            <th className="px-3 py-2 text-left font-medium">Bisagra</th>
                            <th className="px-3 py-2 text-left font-medium">Código EAN</th>
                            <th className="px-3 py-2 text-left font-medium">P. Público</th>
                            <th className="px-3 py-2 text-left font-medium">P. Efectivo</th>
                            <th className="px-3 py-2 text-left font-medium">Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pdfArmazones.map((armazon, idx) => (
                            <tr key={armazon.id_temp} className="border-b hover:bg-gray-50">
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={armazon.marca || ''}
                                  onChange={(e) => {
                                    const updated = [...pdfArmazones];
                                    updated[idx].marca = e.target.value;
                                    setPdfArmazones(updated);
                                  }}
                                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={armazon.modelo || ''}
                                  onChange={(e) => {
                                    const updated = [...pdfArmazones];
                                    updated[idx].modelo = e.target.value;
                                    setPdfArmazones(updated);
                                  }}
                                  className="w-36 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={armazon.color || ''}
                                  onChange={(e) => {
                                    const updated = [...pdfArmazones];
                                    updated[idx].color = e.target.value;
                                    setPdfArmazones(updated);
                                  }}
                                  className="w-32 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </td>
                              <td className="px-3 py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={!!armazon.sol}
                                  onChange={(e) => {
                                    const updated = [...pdfArmazones];
                                    updated[idx] = {
                                      ...updated[idx],
                                      sol: e.target.checked,
                                      ...calcularPreciosPdf(updated[idx].costo, e.target.checked)
                                    };
                                    setPdfArmazones(updated);
                                  }}
                                  title={armazon.contiene_b_cut ? 'Detectado "B CUT": se sugiere no marcar como sol' : ''}
                                  className="rounded border-gray-300"
                                />
                                {armazon.contiene_b_cut && (
                                  <div className="text-[10px] text-amber-600 leading-tight">B CUT</div>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  value={armazon.cantidad}
                                  onChange={(e) => {
                                    const updated = [...pdfArmazones];
                                    updated[idx].cantidad = parseInt(e.target.value) || 0;
                                    setPdfArmazones(updated);
                                  }}
                                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  value={armazon.costo}
                                  onChange={(e) => {
                                    const updated = [...pdfArmazones];
                                    const nuevoCosto = parseFloat(e.target.value) || 0;
                                    updated[idx] = {
                                      ...updated[idx],
                                      costo: nuevoCosto,
                                      ...calcularPreciosPdf(nuevoCosto, updated[idx].sol)
                                    };
                                    setPdfArmazones(updated);
                                  }}
                                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={armazon.material || ''}
                                  onChange={(e) => {
                                    const updated = [...pdfArmazones];
                                    updated[idx].material = e.target.value;
                                    setPdfArmazones(updated);
                                  }}
                                  placeholder="Ej: Acetato"
                                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={armazon.medidas || ''}
                                  onChange={(e) => {
                                    const updated = [...pdfArmazones];
                                    updated[idx].medidas = e.target.value;
                                    setPdfArmazones(updated);
                                  }}
                                  placeholder="Ej: 52-18"
                                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={armazon.tipo_bisagra || ''}
                                  onChange={(e) => {
                                    const updated = [...pdfArmazones];
                                    updated[idx].tipo_bisagra = e.target.value;
                                    setPdfArmazones(updated);
                                  }}
                                  placeholder="Ej: Metal"
                                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={armazon.codigo_ean || ''}
                                  onChange={(e) => {
                                    const updated = [...pdfArmazones];
                                    updated[idx].codigo_ean = e.target.value || null;
                                    setPdfArmazones(updated);
                                  }}
                                  placeholder="Ej: 26001IC..."
                                  className="w-32 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  value={armazon.precio_publico !== undefined && armazon.precio_publico !== null ? armazon.precio_publico : ''}
                                  onChange={(e) => {
                                    const updated = [...pdfArmazones];
                                    updated[idx].precio_publico = parseFloat(e.target.value) || 0;
                                    setPdfArmazones(updated);
                                  }}
                                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  value={armazon.precio_efectivo !== undefined && armazon.precio_efectivo !== null ? armazon.precio_efectivo : ''}
                                  onChange={(e) => {
                                    const updated = [...pdfArmazones];
                                    updated[idx].precio_efectivo = parseFloat(e.target.value) || 0;
                                    setPdfArmazones(updated);
                                  }}
                                  placeholder="Ej: 1500"
                                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <button
                                  onClick={() => {
                                    setPdfArmazones(pdfArmazones.filter((_, i) => i !== idx));
                                  }}
                                  className="text-red-600 hover:text-red-900 font-medium text-sm"
                                >
                                  Quitar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    )}

                    {/* Tabla de Accesorios (THE BAG) */}
                    {tabModalPdf === 'accesorios' && (
                      <div className="space-y-3">
                        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 rounded-md">
                          Los siguientes artículos (ej. bolsos <strong>THE BAG</strong>) se guardarán en la sección de <strong>Productos</strong> bajo la categoría <strong>Accesorios</strong>, con su precio de lista (costo × 3.1) y precio efectivo.
                        </div>
                        <div className="overflow-x-auto border rounded-md">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-100 border-b">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium">Categoría</th>
                                <th className="px-3 py-2 text-left font-medium">Producto / Descripción</th>
                                <th className="px-3 py-2 text-left font-medium">Cant.</th>
                                <th className="px-3 py-2 text-left font-medium">Costo</th>
                                <th className="px-3 py-2 text-left font-medium">P. Lista (x 3.1)</th>
                                <th className="px-3 py-2 text-left font-medium">P. Efectivo</th>
                                <th className="px-3 py-2 text-left font-medium">Acción</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pdfAccesorios.map((acc, idx) => (
                                <tr key={acc.id_temp || idx} className="border-b hover:bg-gray-50">
                                  <td className="px-3 py-2">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                      {acc.tipo || 'Accesorios'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="text"
                                      value={acc.producto || ''}
                                      onChange={(e) => {
                                        const updated = [...pdfAccesorios];
                                        updated[idx].producto = e.target.value;
                                        setPdfAccesorios(updated);
                                      }}
                                      className="w-full min-w-[240px] px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      value={acc.cantidad || 1}
                                      onChange={(e) => {
                                        const updated = [...pdfAccesorios];
                                        updated[idx].cantidad = parseInt(e.target.value) || 1;
                                        setPdfAccesorios(updated);
                                      }}
                                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      value={acc.costo || ''}
                                      onChange={(e) => {
                                        const nuevoCosto = parseFloat(e.target.value) || 0;
                                        const nuevoPLista = redondearAlMillar(nuevoCosto * 3.1);
                                        const updated = [...pdfAccesorios];
                                        updated[idx].costo = nuevoCosto;
                                        updated[idx].precio_lista = nuevoPLista;
                                        updated[idx].precio_efectivo = calcularPrecioEfectivo(nuevoPLista);
                                        setPdfAccesorios(updated);
                                      }}
                                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      value={acc.precio_lista !== undefined && acc.precio_lista !== null ? acc.precio_lista : ''}
                                      onChange={(e) => {
                                        const nuevoPLista = parseFloat(e.target.value) || 0;
                                        const updated = [...pdfAccesorios];
                                        updated[idx].precio_lista = nuevoPLista;
                                        updated[idx].precio_efectivo = calcularPrecioEfectivo(nuevoPLista);
                                        setPdfAccesorios(updated);
                                      }}
                                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      value={acc.precio_efectivo !== undefined && acc.precio_efectivo !== null ? acc.precio_efectivo : ''}
                                      onChange={(e) => {
                                        const updated = [...pdfAccesorios];
                                        updated[idx].precio_efectivo = parseFloat(e.target.value) || 0;
                                        setPdfAccesorios(updated);
                                      }}
                                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPdfAccesorios(pdfAccesorios.filter((_, i) => i !== idx));
                                      }}
                                      className="text-red-600 hover:text-red-900 font-medium text-sm"
                                    >
                                      Quitar
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setModalPdf(false);
                          setPdfArmazones([]);
                          setPdfAccesorios([]);
                          setTabModalPdf('armazones');
                          setPdfArchivo(null);
                          setPdfMarca('');
                          setPdfResumen(null);
                        }}
                        className="flex-1 bg-gray-300 text-gray-900 py-2 px-4 rounded-md hover:bg-gray-400 font-medium"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (pdfArmazones.length === 0 && pdfAccesorios.length === 0) {
                            Swal.fire('Error', 'No hay armazones ni accesorios para guardar', 'error');
                            return;
                          }
                          setPdfGuardando(true);
                          try {
                            const resultado = await guardarArmazonesDesdeModal(
                              pdfArmazones,
                              pdfSol,
                              pdfIdProveedor || null,
                              pdfAccesorios
                            );

                            let mensajeExito = `Se cargaron ${resultado.armazones_insertados || 0} armazones correctamente.`;
                            if (resultado.accesorios_insertados > 0) {
                              mensajeExito += ` Además se agregaron ${resultado.accesorios_insertados} accesorios en la sección de Productos.`;
                            }

                            Swal.fire({
                              title: 'Carga Completada',
                              text: mensajeExito,
                              icon: 'success'
                            });

                            setModalPdf(false);
                            setPdfArmazones([]);
                            setPdfAccesorios([]);
                            setTabModalPdf('armazones');
                            setPdfArchivo(null);
                            setPdfMarca('');
                            setPdfResumen(null);
                            cargarArmazones();

                            if (resultado.armazones_duplicados > 0) {
                              await preguntarPorDuplicadosArmazones(resultado.duplicados);
                            }
                          } catch (err) {
                            Swal.fire('Error', err.message, 'error');
                          } finally {
                            setPdfGuardando(false);
                          }
                        }}
                        disabled={pdfGuardando}
                        className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-60 font-medium"
                      >
                        {pdfGuardando
                          ? 'Guardando...'
                          : pdfArmazones.length > 0 && pdfAccesorios.length > 0
                          ? `Guardar ${pdfArmazones.length} Armazones y ${pdfAccesorios.length} Accesorios`
                          : pdfAccesorios.length > 0
                          ? `Guardar ${pdfAccesorios.length} Accesorios`
                          : `Guardar ${pdfArmazones.length} Armazones`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal de Duplicación */}
        {modalDuplicar && (
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm overflow-y-auto h-full w-full z-[1050] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-90vh overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Duplicar Armazón
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Se mantendrán: Tipo de Bisagra y Proveedor del original
                  </p>
                </div>
                <button
                  onClick={cerrarModalDuplicar}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmitDuplicado} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Marca *</label>
                    {!modoNuevaMarcaDuplicado ? (
                      <select
                        name="marca"
                        value={formDuplicado.marca}
                        onChange={e => {
                          if (e.target.value === '__nuevo__') {
                            setModoNuevaMarcaDuplicado(true);
                            setFormDuplicado(prev => ({ ...prev, marca: '' }));
                          } else {
                            handleChangeDuplicado(e);
                          }
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Seleccionar marca...</option>
                        {optionLegado(formDuplicado.marca, 'leg-dup')}
                        {marcasArmazonesActivas.map((nombre) => <option key={`marca-dup-${nombre}`} value={nombre}>{nombre}</option>)}
                        <option value="__nuevo__">+ Crear nueva marca...</option>
                      </select>
                    ) : (
                      <div className="space-y-1">
                        <input
                          type="text"
                          name="marca"
                          value={formDuplicado.marca}
                          onChange={handleChangeDuplicado}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setModoNuevaMarcaDuplicado(false)}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Usar catálogo existente
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Modelo *</label>
                    <input
                      type="text"
                      name="modelo"
                      value={formDuplicado.modelo}
                      onChange={handleChangeDuplicado}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Color *</label>
                    <input
                      type="text"
                      name="color"
                      value={formDuplicado.color}
                      onChange={handleChangeDuplicado}
                      placeholder="Escribir o seleccionar..."
                      list="coloresArmazonesDuplicado"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <datalist id="coloresArmazonesDuplicado">
                      {(coloresArmazonesCatalogo || [])
                        .filter((i) => i.activo)
                        .map((i) => (
                          <option key={i.id} value={i.nombre} />
                        ))
                      }
                    </datalist>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Material *</label>
                    <input
                      type="text"
                      name="material"
                      value={formDuplicado.material}
                      onChange={handleChangeDuplicado}
                      placeholder="Escribir o seleccionar..."
                      list="materialesArmazonesDuplicado"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <datalist id="materialesArmazonesDuplicado">
                      {(materialesArmazonesCatalogo || [])
                        .filter((i) => i.activo)
                        .map((i) => (
                          <option key={i.id} value={i.nombre} />
                        ))
                      }
                    </datalist>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Cantidad/Stock *</label>
                    <input
                      type="number"
                      name="cantidad"
                      value={formDuplicado.cantidad}
                      onChange={handleChangeDuplicado}
                      min="0"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Medidas</label>
                    <input
                      type="text"
                      name="medidas"
                      value={formDuplicado.medidas}
                      onChange={handleChangeDuplicado}
                      placeholder="ej: 54-18-145"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Código EAN</label>
                    <input
                      type="text"
                      name="codigo_ean"
                      value={formDuplicado.codigo_ean}
                      onChange={handleChangeDuplicado}
                      placeholder="ej: 7890123456789"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Tipo de Bisagra</label>
                    <select
                      name="tipo_bisagra"
                      value={formDuplicado.tipo_bisagra}
                      onChange={handleChangeDuplicado}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Seleccionar tipo...</option>
                      <option value="Estándar">Estándar</option>
                      <option value="Spring">Spring</option>
                      <option value="Sin bisagras">Sin bisagras</option>
                      <option value="Magnética">Magnética</option>
                      <option value="Otros">Otros</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Costo</label>
                    <input
                      type="number"
                      name="costo"
                      value={formDuplicado.costo}
                      onChange={handleChangeDuplicado}
                      min="0"
                      step="0.01"
                      placeholder="Costo unitario"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Precio Público</label>
                    <input
                      type="number"
                      name="precio_publico"
                      value={formDuplicado.precio_publico}
                      onChange={handleChangeDuplicado}
                      min="0"
                      step="0.01"
                      placeholder="Precio de venta"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Efectivo se calcula con descuento {descuentoEfectivo}% (editable).</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Precio Efectivo</label>
                    <input
                      type="number"
                      name="precio_efectivo"
                      value={formDuplicado.precio_efectivo}
                      onChange={handleChangeDuplicado}
                      min="0"
                      step="0.01"
                      placeholder="Auto por descuento"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Proveedor</label>
                    <div className="flex gap-1.5">
                      <select
                        name="id_proveedor"
                        value={formDuplicado.id_proveedor}
                        onChange={handleChangeDuplicado}
                        className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Sin proveedor</option>
                        {proveedores.map((prov) => (
                          <option key={prov.id} value={prov.id}>{prov.nombre}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => handleCrearProveedorRapido(setFormDuplicado, formDuplicado.marca)}
                        className="px-2.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md whitespace-nowrap transition-colors"
                        title="Crear nuevo proveedor"
                      >
                        + Nuevo
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="solDuplicado"
                      checked={formDuplicado.sol}
                      onChange={(e) => setFormDuplicado(prev => ({ ...prev, sol: e.target.checked }))}
                      className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <label htmlFor="solDuplicado" className="text-sm font-medium cursor-pointer">Es anteojo de Sol</label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="en_mlDuplicado"
                      checked={formDuplicado.en_ml}
                      onChange={(e) => setFormDuplicado(prev => ({ ...prev, en_ml: e.target.checked }))}
                      className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <label htmlFor="en_mlDuplicado" className="text-sm font-medium cursor-pointer">Publicado en ML</label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={cerrarModalDuplicar}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={guardandoDuplicado}
                    className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:ring-2 focus:ring-green-500 disabled:bg-gray-400"
                  >
                    {guardandoDuplicado ? 'Guardando...' : 'Crear Duplicado'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Edición */}
        {editando && (
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm overflow-y-auto h-full w-full z-[1050] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-90vh overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b">
                <h3 className="text-lg font-medium text-gray-900">
                  Editar Armazón
                </h3>
                <button
                  onClick={cerrarModalEdicion}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmitEdicion} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Marca *</label>
                    {!modoNuevaMarca.edicion ? (
                      <select
                        name="marca"
                        value={formEdicion.marca}
                        onChange={e => {
                          if (e.target.value === '__nuevo__') {
                            setModoNuevaMarca(prev => ({ ...prev, edicion: true }));
                            setFormEdicion(prev => ({ ...prev, marca: '' }));
                          } else {
                            handleChangeEdicion(e);
                          }
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Seleccionar marca...</option>
                        {optionLegado(formEdicion.marca, 'leg-edicion')}
                        {marcasArmazonesActivas.map((nombre) => <option key={`marca-edicion-${nombre}`} value={nombre}>{nombre}</option>)}
                        <option value="__nuevo__">+ Crear nueva marca...</option>
                      </select>
                    ) : (
                      <div className="space-y-1">
                        <input
                          type="text"
                          name="marca"
                          value={formEdicion.marca}
                          onChange={handleChangeEdicion}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setModoNuevaMarca(prev => ({ ...prev, edicion: false }))}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Usar catálogo existente
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Modelo *</label>
                    <input
                      type="text"
                      name="modelo"
                      value={formEdicion.modelo}
                      onChange={handleChangeEdicion}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Color *</label>
                    <input
                      type="text"
                      name="color"
                      value={formEdicion.color}
                      onChange={handleChangeEdicion}
                      placeholder="Escribir o seleccionar..."
                      list="coloresArmazonesEdicion"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <datalist id="coloresArmazonesEdicion">
                      {(coloresArmazonesCatalogo || [])
                        .filter((i) => i.activo)
                        .map((i) => (
                          <option key={i.id} value={i.nombre} />
                        ))
                      }
                    </datalist>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Material *</label>
                    <input
                      type="text"
                      name="material"
                      value={formEdicion.material}
                      onChange={handleChangeEdicion}
                      placeholder="Escribir o seleccionar..."
                      list="materialesArmazonesEdicion"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <datalist id="materialesArmazonesEdicion">
                      {(materialesArmazonesCatalogo || [])
                        .filter((i) => i.activo)
                        .map((i) => (
                          <option key={i.id} value={i.nombre} />
                        ))
                      }
                    </datalist>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Cantidad/Stock *</label>
                    <input
                      type="number"
                      name="cantidad"
                      value={formEdicion.cantidad}
                      onChange={handleChangeEdicion}
                      min="0"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Medidas</label>
                    <input
                      type="text"
                      name="medidas"
                      value={formEdicion.medidas}
                      onChange={handleChangeEdicion}
                      placeholder="ej: 54-18-145"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Código EAN</label>
                    <input
                      type="text"
                      name="codigo_ean"
                      value={formEdicion.codigo_ean}
                      onChange={handleChangeEdicion}
                      placeholder="ej: 7890123456789"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Tipo de Bisagra</label>
                    <select
                      name="tipo_bisagra"
                      value={formEdicion.tipo_bisagra}
                      onChange={handleChangeEdicion}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Seleccionar tipo...</option>
                      <option value="Estándar">Clásica</option>
                      <option value="Spring">Flex</option>
                      <option value="Sin bisagras">Triple</option>
                      <option value="Magnética">Silicona</option>
                      <option value="Otros">Otros</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Costo</label>
                    <input
                      type="number"
                      name="costo"
                      value={formEdicion.costo}
                      onChange={handleChangeEdicion}
                      min="0"
                      step="0.01"
                      placeholder="Costo unitario"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Precio Público</label>
                    <input
                      type="number"
                      name="precio_publico"
                      value={formEdicion.precio_publico}
                      onChange={handleChangeEdicion}
                      min="0"
                      step="0.01"
                      placeholder="Precio de venta"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Efectivo se calcula con descuento {descuentoEfectivo}% (editable).</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Precio Efectivo</label>
                    <input
                      type="number"
                      name="precio_efectivo"
                      value={formEdicion.precio_efectivo}
                      onChange={handleChangeEdicion}
                      min="0"
                      step="0.01"
                      placeholder="Auto por descuento"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Proveedor</label>
                    <div className="flex gap-1.5">
                      <select
                        name="id_proveedor"
                        value={formEdicion.id_proveedor}
                        onChange={handleChangeEdicion}
                        className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Sin proveedor</option>
                        {proveedores.map((prov) => (
                          <option key={prov.id} value={prov.id}>{prov.nombre}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => handleCrearProveedorRapido(setFormEdicion, formEdicion.marca)}
                        className="px-2.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md whitespace-nowrap transition-colors"
                        title="Crear nuevo proveedor"
                      >
                        + Nuevo
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="solEdicion"
                      checked={formEdicion.sol}
                      onChange={(e) => setFormEdicion(prev => ({ ...prev, sol: e.target.checked }))}
                      className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <label htmlFor="solEdicion" className="text-sm font-medium cursor-pointer">Es anteojo de Sol</label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="en_mlEdicion"
                      checked={formEdicion.en_ml}
                      onChange={(e) => setFormEdicion(prev => ({ ...prev, en_ml: e.target.checked }))}
                      className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <label htmlFor="en_mlEdicion" className="text-sm font-medium cursor-pointer">Publicado en ML</label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={cerrarModalEdicion}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:ring-2 focus:ring-blue-500"
                  >
                    Guardar Cambios
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {modalCatalogosArmazones && (
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto z-[1050] px-4 pt-16 sm:pt-24 pb-6"
            onClick={e => {
              if (e.target === e.currentTarget) {
                setCatalogoArmazonBusqueda('');
                setNuevoCatalogoArmazonNombre('');
                setModalCatalogosArmazones(false);
              }
            }}
          >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[calc(100vh-7rem)] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <BeakerIcon className="h-5 w-5 text-blue-600" />Herramientas de armazones: catálogos
                </h3>
                <button onClick={() => {
                  setCatalogoArmazonBusqueda('');
                  setNuevoCatalogoArmazonNombre('');
                  setModalCatalogosArmazones(false);
                }} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
              </div>

              <div className="p-5 space-y-4 overflow-y-auto flex-1">
                {/* Solapas */}
                <div className="flex flex-wrap gap-2">
                  {catalogoTiposArmazones.map(t => (
                    <button
                      key={t.key}
                      onClick={() => {
                        setCatalogoArmazonActivo(t.key);
                        if (t.key === 'proveedores') {
                          cargarProveedoresModal();
                        }
                      }}
                      className={`px-3 py-1.5 text-sm rounded-lg border-2 font-medium ${
                        catalogoArmazonActivo === t.key
                          ? 'bg-blue-100 text-blue-700 border-blue-300'
                          : 'text-gray-600 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {catalogoArmazonActivo !== 'proveedores' ? (
                  <>
                    {/* Input de creación para catalogos simples */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={nuevoCatalogoArmazonNombre}
                        onChange={e => setNuevoCatalogoArmazonNombre(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                        placeholder={`Nuevo/a ${catalogoArmazonActivo.slice(0, -1)}`}
                      />
                      <button
                        onClick={handleCrearCatalogoArmazon}
                        disabled={guardandoCatalogoArmazon || !nuevoCatalogoArmazonNombre.trim()}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        {guardandoCatalogoArmazon ? 'Guardando...' : 'Agregar'}
                      </button>
                    </div>

                    {/* Input de búsqueda */}
                    <input
                      type="text"
                      value={catalogoArmazonBusqueda}
                      onChange={e => setCatalogoArmazonBusqueda(e.target.value)}
                      placeholder="Buscar por nombre..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                    />

                    {/* Listado de ítems simples */}
                    <div className="border border-gray-200 rounded-lg divide-y max-h-[380px] overflow-y-auto">
                      {itemsCatalogoArmazonActivo.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-6">No hay ítems cargados para este catálogo.</p>
                      )}
                      {itemsCatalogoArmazonActivo.map(item => (
                        <div key={item.id} className="px-4 py-3 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-gray-800">{item.nombre}</p>
                            <p className={`text-xs ${item.activo ? 'text-green-600' : 'text-gray-400'}`}>{item.activo ? 'Activo' : 'Inactivo'}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => handleRenombrarCatalogoArmazon(item)} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">Renombrar</button>
                            <button type="button" onClick={() => handleToggleActivoCatalogoArmazon(item)} className={`px-2 py-1 text-xs rounded ${item.activo ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}>
                              {item.activo ? 'Desactivar' : 'Reactivar'}
                            </button>
                            <button type="button" onClick={() => handleEliminarCatalogoArmazon(item)} className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700">Eliminar</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Formulario de Proveedores */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold mb-3 text-gray-900">
                        {editandoProveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'}
                      </h4>
                      <form onSubmit={handleGuardarProveedor} className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <input type="text" name="nombre" value={formProveedor.nombre} onChange={(e) => setFormProveedor({...formProveedor, nombre: e.target.value})} placeholder="Nombre del proveedor" required className="w-full p-2 border border-gray-300 rounded text-sm" />
                          <input type="text" name="nombre_vendedor" value={formProveedor.nombre_vendedor} onChange={(e) => setFormProveedor({...formProveedor, nombre_vendedor: e.target.value})} placeholder="Nombre del vendedor" className="w-full p-2 border border-gray-300 rounded text-sm" />
                          <input type="tel" name="telefono" value={formProveedor.telefono} onChange={(e) => setFormProveedor({...formProveedor, telefono: e.target.value})} placeholder="Teléfono" className="w-full p-2 border border-gray-300 rounded text-sm" />
                          <input type="text" name="cuit" value={formProveedor.cuit} onChange={(e) => setFormProveedor({...formProveedor, cuit: e.target.value})} placeholder="CUIT" className="w-full p-2 border border-gray-300 rounded text-sm" />
                        </div>
                        <input type="text" name="direccion" value={formProveedor.direccion} onChange={(e) => setFormProveedor({...formProveedor, direccion: e.target.value})} placeholder="Dirección" className="w-full p-2 border border-gray-300 rounded text-sm" />

                        {/* Condiciones comerciales especiales */}
                        <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                          <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                            <TagIcon className="h-4 w-4 text-emerald-600" />
                            Condiciones comerciales personalizadas (opcional)
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Descuento en efectivo (%)
                              </label>
                              <input
                                type="number"
                                name="descuento_efectivo"
                                value={formProveedor.descuento_efectivo}
                                onChange={(e) => setFormProveedor({ ...formProveedor, descuento_efectivo: e.target.value })}
                                placeholder="Ej: 10 (hereda de óptica si vacío)"
                                min="0"
                                max="100"
                                step="0.5"
                                className="w-full p-2 border border-gray-300 rounded text-sm"
                              />
                              <span className="text-[11px] text-gray-500">Aplica a armazones y anteojos de sol de este proveedor.</span>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Máximo de cuotas sin interés
                              </label>
                              <input
                                type="number"
                                name="cuotas_maximas"
                                value={formProveedor.cuotas_maximas}
                                onChange={(e) => setFormProveedor({ ...formProveedor, cuotas_maximas: e.target.value })}
                                placeholder="Ej: 3 (sin tope especial si vacío)"
                                min="1"
                                max="36"
                                step="1"
                                className="w-full p-2 border border-gray-300 rounded text-sm"
                              />
                              <span className="text-[11px] text-gray-500">Tope máximo de financiación para este proveedor.</span>
                            </div>
                          </div>
                        </div>

                        {marcasDisponiblesFiltradasPorProveedor.length > 0 && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Marcas asociadas</label>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 p-2 border border-gray-300 rounded bg-white max-h-32 overflow-y-auto">
                              {marcasDisponiblesFiltradasPorProveedor.map((marca) => (
                                <label key={marca.id} className="flex items-center gap-2 cursor-pointer">
                                  <input type="checkbox" checked={formProveedor.marcas.includes(marca.id)} onChange={() => handleToggleMarcaProveedor(marca.id)} className="w-4 h-4" />
                                  <span className="text-sm text-gray-700">{marca.nombre}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2 pt-2">
                          <button type="submit" disabled={guardandoProveedor} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-400">
                            {guardandoProveedor ? 'Guardando...' : 'Guardar'}
                          </button>
                          {editandoProveedor && (
                            <button type="button" onClick={resetFormProveedor} className="px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
                              Cancelar edición
                            </button>
                          )}
                        </div>
                      </form>
                    </div>

                    {/* Botón para actualizar armazones con proveedores */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-blue-900">Actualizar proveedores en stock</p>
                        <p className="text-xs text-blue-700 mt-0.5">Asignará automáticamente el proveedor a todos los armazones cuyos proveedores ya estén configurados</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleActualizarArmazonesConProveedor}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2 whitespace-nowrap"
                      >
                        <ArrowPathIcon className="h-4 w-4" />
                        {loading ? 'Actualizando...' : 'Actualizar'}
                      </button>
                    </div>

                    {/* Búsqueda de proveedores */}
                    <input type="text" value={catalogoArmazonBusqueda} onChange={(e) => setCatalogoArmazonBusqueda(e.target.value)} placeholder="Buscar por nombre, vendedor o teléfono..." className="w-full p-2 border border-gray-300 rounded text-sm" />

                    {/* Listado de proveedores */}
                    {cargandoProveedores ? (
                      <div className="text-center py-4 text-gray-500">Cargando...</div>
                    ) : itemsCatalogoArmazonActivo.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">No hay proveedores</div>
                    ) : (
                      <div className="border border-gray-200 rounded divide-y max-h-[350px] overflow-y-auto">
                        {itemsCatalogoArmazonActivo.map((proveedor) => (
                          <div key={proveedor.id} className="p-3 hover:bg-gray-50">
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{proveedor.nombre}</p>
                                {proveedor.nombre_vendedor && <p className="text-xs text-gray-600">Vendedor: {proveedor.nombre_vendedor}</p>}
                                <div className="grid grid-cols-2 gap-2 mt-1 text-xs text-gray-600">
                                  {proveedor.telefono && <p>Tel: {proveedor.telefono}</p>}
                                  {proveedor.cuit && <p>CUIT: {proveedor.cuit}</p>}
                                  {proveedor.direccion && <p className="col-span-2">Dir: {proveedor.direccion}</p>}
                                </div>
                              </div>
                              <div className="flex gap-2 flex-shrink-0">
                                <button onClick={() => handleEditarProveedor(proveedor)} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">Editar</button>
                                <button onClick={() => handleEliminarProveedor(proveedor)} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200">Eliminar</button>
                              </div>
                            </div>
                            {proveedor.marcas && proveedor.marcas.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {proveedor.marcas.map((marca) => (
                                  <span key={marca.id} className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                    {marca.nombre}
                                  </span>
                                ))}
                              </div>
                            )}

                            {(proveedor.descuento_efectivo != null || proveedor.cuotas_maximas != null) && (
                              <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-gray-100 text-xs">
                                {proveedor.descuento_efectivo != null && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md font-medium">
                                    <TagIcon className="h-3.5 w-3.5 text-emerald-600" />
                                    Desc. efectivo: {proveedor.descuento_efectivo}%
                                  </span>
                                )}
                                {proveedor.cuotas_maximas != null && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-md font-medium">
                                    <CreditCardIcon className="h-3.5 w-3.5 text-indigo-600" />
                                    Máx. {proveedor.cuotas_maximas} cuotas sin interés
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <NuevoProveedorRapidoModal
          isOpen={Boolean(modalProveedorRapidoContext)}
          onClose={() => setModalProveedorRapidoContext(null)}
          proveedoresExistentes={proveedores}
          marcaActualNombre={modalProveedorRapidoContext?.marcaActual || ''}
          marcasCatalogo={marcasArmazonesCatalogo}
          onProveedorCreado={async (provCreado) => {
            await cargarProveedoresModal();
            if (provCreado?.id && modalProveedorRapidoContext?.setterFn) {
              modalProveedorRapidoContext.setterFn((prev) => ({
                ...prev,
                id_proveedor: String(provCreado.id),
              }));
            }
          }}
        />
      </div>
    </div>
  );
};

export default StockArmazones;
